import { useEffect, useRef, useState } from 'react';
import { del, get, patch, post, qs } from '../lib/api';
import { navigate, useLoad, useRoute } from '../lib/hooks';
import { useSession } from '../lib/session';
import { formatCents, formatRatePpm, parseRatePercent } from '../../shared/money';
import { SOPHIA_ARABIC_CODES, SOPHIA_HEARING, SOPHIA_VOICES } from '../../shared/sophia';
import { formatDateTime, formatPhone } from '../../shared/format';
import { Badge, Button, Confirm, Empty, Field, MoneyInput, Sheet, Spinner, Tabs, useAction, useToast } from '../ui/kit';
import { ACTIVATION_CARRIERS, commissionRatesText, parseCommissionRates } from '../../shared/orders';
import { QrCode } from '../ui/qr';
import { RegisterDeviceSheet } from './Gate';
import { GoalsSettingsTab } from './Goals';
import { ORDER_LABEL_SIZES, PHORA_MARKUP_TIERS } from '../../shared/parts';
import { BUY_LABEL_SIZES } from '../../shared/buyback';
import { UNLOCK_API_DEFAULTS, UNLOCK_API_SLOTS, unlockPriceLabel } from '../../shared/unlock';
import { useCloudPrint } from '../lib/printing';
import { TECH_TICKET_FORMATS, isTechTicketFormat, type TechTicketFormat } from '../../shared/repairs';
import { PRICE_STICKER_SIZES, priceStickerShiftMm, priceStickerSize, type PriceStickerSize } from '../../shared/receipts';
import { Icon } from '../ui/icons';
import { usePlan } from '../lib/plan';
import { BillingTab } from './Billing';
import { SaleMenuTab } from './SaleMenuSettings';

type Tab = 'goals' | 'stores' | 'business' | 'menu' | 'printers' | 'clover' | 'points' | 'buy' | 'orders' | 'messages' | 'import' | 'tablets' | 'audit' | 'billing';

export function SettingsScreen({ tab }: { tab?: string }) {
  const { can, me, status } = useSession();
  const plan = usePlan();
  // West Coast Wireless's own integrations (Clover, Shopify, texting and calls, RepairDesk) are its store's only, for now.
  const home = plan.home;
  const owner = me?.employee.role_id === 'owner';
  const tabs = [
    plan.subscriber && owner ? { id: 'billing' as Tab, label: 'Billing' } : null,
    can('settings.manage') ? { id: 'stores' as Tab, label: 'Stores & tax' } : null,
    can('settings.manage') ? { id: 'business' as Tab, label: 'Business & receipts' } : null,
    can('settings.manage') ? { id: 'menu' as Tab, label: 'Sale menu' } : null,
    owner ? { id: 'goals' as Tab, label: 'Goals' } : null,
    can('settings.manage') ? { id: 'printers' as Tab, label: 'Printers' } : null,
    can('settings.manage') && home ? { id: 'clover' as Tab, label: 'Clover payments' } : null,
    can('settings.manage') ? { id: 'points' as Tab, label: home ? 'Points & Shopify' : 'Points' } : null,
    can('settings.manage') && plan.has('buybacks') && plan.industry === 'cellphone' ? { id: 'buy' as Tab, label: 'Buy & trade-in' } : null,
    can('settings.manage') && plan.has('special_orders') ? { id: 'orders' as Tab, label: 'Special orders' } : null,
    can('settings.manage') && home ? { id: 'messages' as Tab, label: 'Messages & calls' } : null,
    can('settings.manage') && home ? { id: 'import' as Tab, label: 'RepairDesk import' } : null,
    can('registers.manage') ? { id: 'tablets' as Tab, label: 'Store tablets' } : null,
    can('audit.view') && plan.has('audit_log') ? { id: 'audit' as Tab, label: 'Audit log' } : null,
  ].filter(Boolean) as { id: Tab; label: string }[];
  const current = (tabs.find((t) => t.id === tab)?.id ?? tabs[0]?.id) as Tab;
  return (
    <div className="page">
      <h1>Settings</h1>
      <Tabs label="Settings" value={current} onChange={(t) => navigate(`/settings/${t}`)} tabs={tabs} />
      {status?.demo_site && ['printers', 'clover', 'points', 'buy', 'orders', 'messages', 'import'].includes(current) ? (
        <div className="banner">
          <Icon.info />
          <div><div className="strong">Demo: outside services are switched off.</div><div className="small">Printers, card readers, texting and calls, IMEI checks, Shopify, RepairDesk, email and package tracking can’t be connected in this sample shop, so their keys won’t save here. Everything else on this page works.</div></div>
        </div>
      ) : null}
      {current === 'billing' ? <BillingTab /> : null}
      {current === 'stores' ? <StoresTab /> : null}
      {current === 'business' ? <BusinessTab /> : null}
      {current === 'menu' ? <SaleMenuTab /> : null}
      {current === 'goals' ? <GoalsSettingsTab /> : null}
      {current === 'printers' ? <PrintersTab /> : null}
      {current === 'clover' ? <CloverTab /> : null}
      {current === 'points' ? <PointsTab /> : null}
      {current === 'buy' ? <BuyTab /> : null}
      {current === 'orders' ? <OrdersTab /> : null}
      {current === 'messages' ? <MessagesTab /> : null}
      {current === 'import' ? <ImportTab /> : null}
      {current === 'tablets' ? <TabletsTab /> : null}
      {current === 'audit' ? <AuditTab /> : null}
    </div>
  );
}

function StoresTab() {
  const { can, me, refresh } = useSession();
  const owner = can('settings.manage');
  const locs = useLoad<any[]>(() => get(owner ? '/api/locations/all' : '/api/locations'), [owner]);
  const { run, busy } = useAction();
  const [adding, setAdding] = useState(false);
  const all = locs.data;
  const open = all?.filter((l) => l.active !== 0) ?? [];
  const closed = all?.filter((l) => l.active === 0) ?? [];
  const setOpen = async (l: any, isOpen: boolean) => {
    const r = await run(() => post(`/api/locations/${l.id}/open`, { open: isOpen }), { success: isOpen ? `${l.name} is open in STORE OS.` : `${l.name} is closed in STORE OS.` });
    if (r) { locs.reload(); refresh(); }
  };
  return (
    <div className="stack">
      {owner ? <div className="row-wrap"><Button icon="plus" onClick={() => setAdding(true)}>Add a store</Button></div> : null}
      {all ? open.map((l) => <StoreCard key={l.id} loc={l} onSaved={locs.reload} onCloseStore={owner && l.id !== me?.location?.id && open.length > 1 ? () => setOpen(l, false) : undefined} />) : <Spinner />}
      {closed.length ? (
        <div className="card stack-sm">
          <h2>Not open in STORE OS</h2>
          <p className="small muted">Open a store to set up its tablet, sell and check in repairs there, and see it in reports.</p>
          {closed.map((l) => (
            <div key={l.id} className="spread">
              <span><strong>{l.name}</strong> <span className="muted small">{[l.address1, l.city].filter(Boolean).join(', ')}</span></span>
              <Button size="sm" variant="primary" busy={busy} onClick={() => setOpen(l, true)}>Open in STORE OS</Button>
            </div>
          ))}
        </div>
      ) : null}
      <p className="small muted">Sales tax rates come from the California CDTFA rate table. Check them each quarter; rates change on Jan 1, Apr 1, Jul 1 and Oct 1.</p>
      {adding ? <AddStoreSheet onClose={() => setAdding(false)} onDone={() => { setAdding(false); locs.reload(); refresh(); }} /> : null}
    </div>
  );
}

/** A new West Coast Wireless store: it opens in STORE OS right away. */
function AddStoreSheet({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const { run, busy } = useAction();
  const [f, setF] = useState({ name: '', address1: '', address2: '', city: '', state: 'CA', zip: '', phone: '', rate: '' });
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement>) => setF({ ...f, [k]: e.target.value });
  const ppm = parseRatePercent(f.rate);
  const save = async () => {
    const r = await run(() => post('/api/locations', {
      name: f.name.trim(), address1: f.address1.trim() || null, address2: f.address2.trim() || null, city: f.city.trim() || null, state: f.state.trim() || null,
      zip: f.zip.trim() || null, phone: f.phone.replace(/\D/g, '') || null, taxRatePpm: ppm,
    }), { success: `${f.name.trim()} added and open in STORE OS.` });
    if (r) onDone();
  };
  return (
    <Sheet title="Add a store" onClose={onClose} footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" busy={busy} disabled={f.name.trim().length < 2 || ppm === null || !f.rate.trim()} onClick={save}>Add store</Button></>}>
      <div className="form-grid">
        <Field label="Store name" hint="like Sacramento"><input className="input" value={f.name} onChange={set('name')} autoFocus /></Field>
        <Field label="Sales tax rate %"><input className="input" inputMode="decimal" value={f.rate} onChange={set('rate')} placeholder="e.g. 8.75" aria-invalid={!!f.rate.trim() && ppm === null} /></Field>
        <Field label="Street address"><input className="input" value={f.address1} onChange={set('address1')} /></Field>
        <Field label="Suite"><input className="input" value={f.address2} onChange={set('address2')} /></Field>
        <Field label="City"><input className="input" value={f.city} onChange={set('city')} /></Field>
        <Field label="State"><input className="input" value={f.state} onChange={set('state')} maxLength={2} /></Field>
        <Field label="ZIP"><input className="input" value={f.zip} onChange={set('zip')} /></Field>
        <Field label="Store phone"><input className="input" inputMode="tel" value={f.phone} onChange={set('phone')} /></Field>
      </div>
    </Sheet>
  );
}

function StoreCard({ loc, onSaved, onCloseStore }: { loc: any; onSaved: () => void; onCloseStore?: () => void }) {
  const { run, busy } = useAction();
  const [f, setF] = useState({ rate: formatRatePpm(loc.tax_rate_ppm).replace('%', ''), phone: formatPhone(loc.phone), address1: loc.address1 ?? '', address2: loc.address2 ?? '', city: loc.city ?? '', zip: loc.zip ?? '', footer: loc.receipt_footer ?? '' });
  const ppm = parseRatePercent(f.rate);
  const save = async () => {
    const r = await run(() => patch(`/api/locations/${loc.id}`, { taxRatePpm: ppm ?? undefined, phone: f.phone.replace(/\D/g, '') || null, address1: f.address1, address2: f.address2 || null, city: f.city, zip: f.zip, receiptFooter: f.footer || null }), { success: `${loc.name} saved.` });
    if (r) onSaved();
  };
  return (
    <div className="card stack">
      <div className="spread"><h2>{loc.name}</h2><Badge>Hub store #{loc.hub_id ?? '—'}</Badge></div>
      <div className="form-grid">
        <Field label="Sales tax rate %"><input className="input" inputMode="decimal" value={f.rate} onChange={(e) => setF({ ...f, rate: e.target.value })} aria-invalid={ppm === null} /></Field>
        <Field label="Store phone"><input className="input" inputMode="tel" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} /></Field>
        <Field label="Street address"><input className="input" value={f.address1} onChange={(e) => setF({ ...f, address1: e.target.value })} /></Field>
        <Field label="Suite"><input className="input" value={f.address2} onChange={(e) => setF({ ...f, address2: e.target.value })} /></Field>
        <Field label="City"><input className="input" value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} /></Field>
        <Field label="ZIP"><input className="input" value={f.zip} onChange={(e) => setF({ ...f, zip: e.target.value })} /></Field>
        <Field label="Receipt message for this store" hint="optional" className="full"><input className="input" value={f.footer} onChange={(e) => setF({ ...f, footer: e.target.value })} /></Field>
      </div>
      <StoreTeam loc={loc} />
      <div className="row-wrap">
        <Button variant="primary" busy={busy} disabled={ppm === null} onClick={save}>Save {loc.name}</Button>
        {onCloseStore ? <Button variant="ghost" onClick={onCloseStore}>Close in STORE OS</Button> : null}
      </div>
    </div>
  );
}

function BusinessTab() {
  const s = useLoad(() => get('/api/settings'), []);
  const { refresh } = useSession();
  const plan = usePlan();
  const home = plan.home;
  const phones = plan.industry === 'cellphone';
  const { run, busy } = useAction();
  const [f, setF] = useState<Record<string, string>>({});
  useEffect(() => { if (s.data) setF(s.data); }, [s.data]);
  if (!s.data) return <Spinner />;
  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement>) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    const changed = Object.fromEntries(Object.entries(f).filter(([k, v]) => s.data[k] !== v));
    const r = await run(() => patch('/api/settings', changed), { success: 'Saved.' });
    if (r) { s.reload(); refresh(); }
  };
  return (
    <div className="stack">
      <div className="card stack">
        <div className="form-grid">
          <Field label="Business name" hint="on screens and receipts"><input className="input" value={f.business_name ?? ''} onChange={set('business_name')} /></Field>
          <Field label="Website"><input className="input" value={f.business_website ?? ''} onChange={set('business_website')} /></Field>
          <Field label="Receipt logo" hint={home ? 'the West Coast Wireless logo is built in (/assets/wcw-receipt-logo.png); or paste a link to another picture' : 'paste a link to your logo (https://…); a black-and-white PNG prints best. Empty: your business name prints instead.'} className="full"><input className="input" value={f.receipt_logo_url ?? ''} onChange={set('receipt_logo_url')} /></Field>
          <Field label="Receipt message" className="full"><input className="input" value={f.receipt_footer ?? ''} onChange={set('receipt_footer')} /></Field>
          <Field label="Discount limit for sales staff %" hint="bigger discounts need a manager"><input className="input" inputMode="numeric" value={f.sales_discount_limit_pct ?? ''} onChange={set('sales_discount_limit_pct')} /></Field>
          <Field label="Lock tablets after (minutes idle)"><input className="input" inputMode="numeric" value={f.idle_lock_minutes ?? ''} onChange={set('idle_lock_minutes')} /></Field>
          {home ? <Field label="WCW Hub address" className="full"><input className="input" value={f.hub_base_url ?? ''} onChange={set('hub_base_url')} /></Field> : null}
          <Field label="Member code prefix" hint={`member cards look like ${(f.ticket_prefix || 'ABC').toUpperCase()}-7K3Q9P2X; repair tickets are T-1001, T-1002…`}><input className="input" value={f.ticket_prefix ?? ''} onChange={set('ticket_prefix')} maxLength={6} /></Field>
          <Field label="Parts share of a repair price, %" hint="taxed when no part from stock is attached; labor isn't taxed"><input className="input" inputMode="numeric" value={f.repair_parts_share_pct ?? ''} onChange={set('repair_parts_share_pct')} /></Field>
          {home ? (
            <>
              <Field label="Sales tax report email" hint="parts only; empty sends it to the owner’s email"><input className="input" inputMode="email" value={f.tax_report_email ?? ''} onChange={set('tax_report_email')} placeholder="Empty: the owner’s email" /></Field>
              <Field label="Send the sales tax report">
                <select className="select" value={f.tax_report_schedule ?? 'monthly'} onChange={(e) => setF({ ...f, tax_report_schedule: e.target.value })} aria-label="Send the sales tax report">
                  <option value="monthly">On the 1st of every month</option>
                  <option value="quarterly">After each quarter</option>
                  <option value="off">Don’t email it</option>
                </select>
              </Field>
              <Field label="Repair price from a part’s cost" className="full" hint={'for MobileSentrix parts on the repair menu. One line per price band, "under $ | add $": "20 | 90" adds $90 to parts under $20. The last line is "over | 250". Prices round up to the next $5.'}><textarea className="input" rows={7} value={f.repair_part_markup ?? ''} onChange={(e) => setF({ ...f, repair_part_markup: e.target.value })} aria-label="Repair price from a part’s cost" /></Field>
            </>
          ) : null}
          <Field label={phones ? 'Unlock / reset services' : 'Service menu'} className="full" hint={'one per line: "Name | price" (labor only, not taxed)'}><textarea className="input" rows={5} value={f.service_menu ?? ''} onChange={(e) => setF({ ...f, service_menu: e.target.value })} /></Field>
          {home ? <Field label="Unlock sites" className="full" hint={'one per line: "Name | https://link". They open beside STORE OS from Unlock and Phone check, with the IMEI copied.'}><textarea className="input" rows={4} value={f.unlock_links ?? ''} onChange={(e) => setF({ ...f, unlock_links: e.target.value })} placeholder="HankUnlock | https://…" /></Field> : null}
          {phones ? (
            <>
              <Field label="Bill pay fee" hint="the usual service fee"><input className="input" inputMode="decimal" value={f.bill_pay_fee ?? ''} onChange={set('bill_pay_fee')} /></Field>
              <Field label="Activation fee" hint="the usual fee"><input className="input" inputMode="decimal" value={f.activation_fee ?? ''} onChange={set('activation_fee')} /></Field>
              <Field label="Carrier portal link" className="full" hint={home ? 'opens from Bill pay and Activation (VidaPay)' : 'the carrier or payment portal you use; opens from Bill pay and Activation'}><input className="input" value={f.carrier_portal_url ?? ''} onChange={set('carrier_portal_url')} /></Field>
            </>
          ) : null}
        </div>
        <div><Button variant="primary" busy={busy} onClick={save}>Save</Button></div>
      </div>
      {phones ? <div className="card stack">
        <div className="spread"><h2>Carrier commissions</h2><Badge tone="plain" icon="lock">Owner only</Badge></div>
        <p className="small muted">What each carrier pays the store for an activation. Staff never see these; they’re added to each activation automatically and show in Reports by carrier, store, employee and day. Never on the customer’s bill. Leave a carrier blank until you know its rate: its activations wait and are counted once you enter it.</p>
        <div className="commission-grid">
          {ACTIVATION_CARRIERS.map((c) => {
            const rates = parseCommissionRates(f.activation_commission_rates ?? '') ?? new Map();
            return (
              <Field key={c} label={c} hint={rates.get(c) == null ? 'not set yet' : 'per activation'}>
                <MoneyInput value={rates.get(c) ?? null} ariaLabel={`${c} commission per activation`} onChange={(v) => { rates.set(c, v); setF({ ...f, activation_commission_rates: commissionRatesText(rates) }); }} />
              </Field>
            );
          })}
        </div>
        <div><Button variant="primary" busy={busy} onClick={save}>Save commissions</Button></div>
      </div> : null}
      <div className="card stack">
        <h2>Policies on receipts</h2>
        <p className="small muted">Printed at the bottom of each thermal receipt, one point per line, under the logo and the store’s details at the top. Change them to fit your store.</p>
        <div className="form-grid">
          {([
            ['policy_sale', 'Sales receipts', true],
            ['repair_terms', plan.industry === 'auto' ? 'Work orders' : 'Repair tickets', true],
            ['policy_order', 'Special order receipts', plan.has('special_orders')],
            ['policy_buy', 'Buy and trade-in receipts', phones && plan.has('buybacks')],
          ] as const).filter(([, , show]) => show).map(([k, label]) => (
            <Field key={k} label={label} className="full"><textarea className="input" rows={5} value={f[k] ?? ''} onChange={(e) => setF({ ...f, [k]: e.target.value })} /></Field>
          ))}
        </div>
        <div><Button variant="primary" busy={busy} onClick={save}>Save policies</Button></div>
      </div>
      <div className="card stack-sm">
        <h2>Backup</h2>
        <p className="small muted">Download a copy of everything: sales, customers, repairs, phones bought, points and the audit log. Do it once a week. It has customer details and seller IDs, so keep it private (for example in your own Google Drive). API keys aren’t included.</p>
        <div><a className="btn btn-secondary" href="/api/backup" download={`store-os-backup-${new Date().toISOString().slice(0, 10)}.db`}>Download a backup</a></div>
      </div>
    </div>
  );
}

/**
 * Who works at a store: their names show on that store's tablet, and they sign in there with
 * their own code. "Add the whole team" lets everyone work there (a new store, at first).
 */
function StoreTeam({ loc }: { loc: any }) {
  const { can } = useSession();
  const emps = useLoad<any[]>(() => (can('employees.manage') ? get('/api/employees') : Promise.resolve([])), []);
  const { run, busy } = useAction();
  if (!can('employees.manage') || !emps.data?.length) return null;
  const people = emps.data;
  const here = (e: any) => e.all_locations || e.location_ids.includes(loc.id);
  const setHere = (e: any, on: boolean) => patch(`/api/employees/${e.id}`, {
    locationIds: on ? [...new Set([...e.location_ids, loc.id])] : e.location_ids.filter((x: string) => x !== loc.id),
  });
  const missing = people.filter((e) => !here(e));
  const toggle = async (e: any) => {
    const on = !here(e);
    const r = await run(() => setHere(e, on), { success: on ? `${e.name} can work at ${loc.name}.` : `${e.name} no longer works at ${loc.name}.` });
    if (r) emps.reload();
  };
  const addAll = async () => {
    const r = await run(async () => { for (const e of missing) await setHere(e, true); return true; }, { success: `The whole team can work at ${loc.name}.` });
    if (r) emps.reload();
  };
  return (
    <div className="stack-sm">
      <div className="spread">
        <span className="small strong">Who works at {loc.name}</span>
        {missing.length ? <Button size="sm" icon="users" busy={busy} onClick={addAll}>Add the whole team</Button> : null}
      </div>
      <div className="chips">
        {people.map((e) => {
          const fixed = e.all_locations || e.home_location_id === loc.id;
          return (
            <button key={e.id} type="button" className="chip" aria-pressed={here(e)} disabled={busy || fixed} onClick={() => toggle(e)}
              title={e.all_locations ? 'Works at every store' : e.home_location_id === loc.id ? 'Home store' : here(e) ? `Tap to take ${e.name} off ${loc.name}` : `Tap to let ${e.name} work at ${loc.name}`}>
              {e.name}{e.all_locations ? ' · all stores' : e.home_location_id === loc.id ? ' · home store' : ''}
            </button>
          );
        })}
      </div>
      <p className="small muted">Their names show on the {loc.name} tablet and they sign in there with their own code.</p>
    </div>
  );
}

/** Setting up a computer at another store from here: a one-time link to open on that computer. */
function SetupLinks() {
  const links = useLoad<any[]>(() => get('/api/setup-links'), []);
  const [making, setMaking] = useState(false);
  const { run, busy } = useAction();
  const cancel = async (l: any) => {
    const r = await run(() => del(`/api/setup-links/${l.id}`), { success: 'Setup link cancelled.' });
    if (r) links.reload();
  };
  return (
    <div className="card stack-sm">
      <div className="row-wrap">
        <div className="grow">
          <h2>Set up a computer at a store</h2>
          <p className="small muted">For a store you’re not at, like Hayward or San Francisco: make a link, text or email it to someone there, and they open it on the store’s computer. It works once, for 24 hours. Staff still sign in with their own codes.</p>
        </div>
        <Button variant="primary" icon="plus" onClick={() => setMaking(true)}>Make a setup link</Button>
      </div>
      {links.data?.length ? (
        <div className="list">
          {links.data.map((l: any) => (
            <div key={l.id} className="list-row" style={{ cursor: 'default' }}>
              <span className="main-col">
                <span className="t" style={{ display: 'block' }}>{l.location_name} · {l.name}</span>
                <span className="s" style={{ display: 'block' }}>Made {formatDateTime(l.created_at)}{l.created_by_name ? ` by ${l.created_by_name}` : ''} · works until {formatDateTime(l.expires_at)}</span>
              </span>
              <Button size="sm" busy={busy} onClick={() => cancel(l)}>Cancel link</Button>
            </div>
          ))}
        </div>
      ) : null}
      {making ? <SetupLinkSheet onClose={() => { setMaking(false); links.reload(); }} /> : null}
    </div>
  );
}

function SetupLinkSheet({ onClose }: { onClose: () => void }) {
  const { me } = useSession();
  const toast = useToast();
  const stores = me?.locations ?? [];
  const [store, setStore] = useState(() => stores.find((l) => l.id !== me?.location?.id)?.id ?? stores[0]?.id ?? '');
  const [name, setName] = useState('Front counter');
  const [made, setMade] = useState<{ url: string; location: string; expires_at: number } | null>(null);
  const { run, busy } = useAction();
  const make = async () => {
    const r = await run(() => post('/api/setup-links', { locationId: store, name: name.trim() || null }));
    if (r) setMade({ url: `${location.origin}/#/setup/${r.token}`, location: r.location.name, expires_at: r.expires_at });
  };
  const copy = async () => {
    if (!made) return;
    try {
      await navigator.clipboard.writeText(made.url);
      toast('Link copied. Paste it in a text or email.', 'good');
    } catch {
      toast('Copy the link by hand: press and hold it, then Copy.', 'info');
    }
  };
  return (
    <Sheet
      title="Set up a computer at a store"
      onClose={onClose}
      footer={made ? <Button variant="primary" onClick={onClose}>Done</Button> : <><Button onClick={onClose}>Cancel</Button><Button variant="primary" busy={busy} disabled={!store} onClick={make}>Make the link</Button></>}
    >
      {made ? (
        <div className="stack">
          <p>Open this link on the <strong>{made.location}</strong> computer, then tap <strong>Set up this computer</strong>. It works once, until {formatDateTime(made.expires_at)}.</p>
          <div className="setup-link" aria-label="Setup link">{made.url}</div>
          <div className="row-wrap"><Button variant="primary" icon="send" onClick={copy}>Copy link</Button></div>
          <div className="stack-sm" style={{ alignItems: 'center', textAlign: 'center' }}>
            <QrCode text={made.url} size={200} label="Setup link QR code" />
            <p className="small muted">Or scan this with the store tablet’s camera.</p>
          </div>
        </div>
      ) : (
        <div className="stack">
          <Field label="Store">
            <select className="select" value={store} onChange={(e) => setStore(e.target.value)}>
              {stores.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          </Field>
          <Field label="Name for the computer" hint="like Front counter or Repair bench"><input className="input" value={name} onChange={(e) => setName(e.target.value)} maxLength={40} /></Field>
          <p className="small muted">Whoever opens the link on that computer makes it the store’s tablet, without your email or code. Send it only to someone at that store.</p>
        </div>
      )}
    </Sheet>
  );
}

function TabletsTab() {
  const { me } = useSession();
  const regs = useLoad(() => get('/api/registers'), []);
  const [block, setBlock] = useState<any>(null);
  const [adding, setAdding] = useState(false);
  const { run, busy } = useAction();
  const doBlock = async () => {
    const r = await run(() => del(`/api/registers/${block.id}`), { success: 'Tablet blocked.' });
    setBlock(null);
    if (r) regs.reload();
  };
  return (
    <div className="stack">
      <div className="row-wrap">
        <p className="muted grow">Store tablets stay registered to one store. Block one right away if it's lost or stolen.</p>
        {!me?.register ? <Button variant="primary" onClick={() => setAdding(true)}>Make this device a store tablet</Button> : null}
      </div>
      <SetupLinks />
      {regs.data?.length ? (
        <div className="list">
          {regs.data.map((r: any) => (
            <div key={r.id} className="list-row" style={{ cursor: 'default' }}>
              <span className="main-col">
                <span className="t" style={{ display: 'block' }}>{r.name} · {r.location_name}</span>
                <span className="s" style={{ display: 'block' }}>Added {formatDateTime(r.created_at)}{r.created_by_name ? ` by ${r.created_by_name}` : ''} · last used {r.last_seen_at ? formatDateTime(r.last_seen_at) : 'never'}</span>
              </span>
              {r.id === me?.register?.id ? <Badge tone="blue">This device</Badge> : null}
              {r.revoked_at ? <Badge tone="red">Blocked</Badge> : <Button variant="danger" size="sm" onClick={() => setBlock(r)}>Block</Button>}
            </div>
          ))}
        </div>
      ) : regs.data ? <Empty icon="store" title="No store tablets yet." /> : <Spinner />}
      {block ? <Confirm title={`Block "${block.name}"?`} confirmLabel="Block tablet" danger busy={busy} onClose={() => setBlock(null)} onConfirm={doBlock}>Anyone using it is signed out immediately. You'd have to register it again to use it.</Confirm> : null}
      {adding ? <RegisterDeviceSheet onClose={() => { setAdding(false); regs.reload(); }} /> : null}
    </div>
  );
}

function AuditTab() {
  const { me } = useSession();
  const [loc, setLoc] = useState('');
  const [action, setAction] = useState('');
  const [rows, setRows] = useState<any[]>([]);
  const [done, setDone] = useState(false);
  const first = useLoad(() => get(`/api/audit${qs({ location: loc, action, limit: 100 })}`), [loc, action]);
  useEffect(() => { if (first.data) { setRows(first.data); setDone(first.data.length < 100); } }, [first.data]);
  const more = async () => {
    const last = rows[rows.length - 1];
    const next = await get(`/api/audit${qs({ location: loc, action, before: last?.at, limit: 100 })}`);
    setRows([...rows, ...next]);
    setDone(next.length < 100);
  };
  return (
    <div className="stack">
      <div className="row-wrap">
        <select className="select" style={{ width: 'auto' }} value={loc} onChange={(e) => setLoc(e.target.value)} aria-label="Store">
          <option value="">All my stores</option>
          {me?.locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
        </select>
        <select className="select" style={{ width: 'auto' }} value={action} onChange={(e) => setAction(e.target.value)} aria-label="Type">
          <option value="">Everything</option>
          <option value="sale">Sales and refunds</option>
          <option value="product">Product prices and costs</option>
          <option value="device">Phones</option>
          <option value="stock">Stock counts</option>
          <option value="transfer">Transfers</option>
          <option value="drawer">Cash drawer</option>
          <option value="employee">Employees</option>
          <option value="login">Sign-ins</option>
          <option value="store">Store settings</option>
        </select>
      </div>
      <p className="small muted">The audit log can't be edited or deleted by anyone.</p>
      {rows.length ? (
        <div className="list">
          <table className="table">
            <thead><tr><th>When</th><th>Who</th><th>Store</th><th>What happened</th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td className="nowrap small">{formatDateTime(r.at)}</td>
                  <td className="small">{r.employee_name || '—'}{r.approved_by_name ? <div className="tiny muted">approved by {r.approved_by_name}</div> : null}</td>
                  <td className="small">{r.location_name ?? '—'}</td>
                  <td>{r.summary}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : first.data ? <Empty icon="history" title="Nothing logged yet." /> : <Spinner />}
      {!done && rows.length ? <Button onClick={more}>Load older</Button> : null}
    </div>
  );
}

/** Card payments on each store's Clover Mini: the app, the connection to the Clover account, and each store's Mini. */
function CloverTab() {
  const setup = useLoad(() => get('/api/clover/setup'), []);
  const route = useRoute();
  const { refresh } = useSession();
  const { run, busy } = useAction();
  const toast = useToast();
  const [appId, setAppId] = useState('');
  const [secret, setSecret] = useState('');
  const [serials, setSerials] = useState<Record<string, string>>({});
  const [testing, setTesting] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<Record<string, string>>({});
  // Back from Clover's sign-in page: say how it went, once.
  const shown = useRef(false);
  useEffect(() => {
    const r = route.query.get('clover');
    if (!r || shown.current) return;
    shown.current = true;
    toast(route.query.get('m') || (r === 'connected' ? 'Clover connected.' : 'Clover didn’t connect.'), r === 'connected' ? 'good' : 'error');
    navigate('/settings/clover');
  }, [route.query, toast]);
  useEffect(() => {
    if (!setup.data) return;
    setAppId(setup.data.app_id_env ? '' : setup.data.app_id);
    setSerials(Object.fromEntries(setup.data.stores.map((st: any) => [st.id, st.serial])));
  }, [setup.data]);
  const d = setup.data;
  if (!d) return <Spinner />;
  const saveApp = async () => {
    const body: Record<string, string> = { clover_env: d.env };
    if (appId.trim() !== (d.app_id_env ? '' : d.app_id)) body.clover_app_id = appId.trim();
    if (secret.trim()) body.clover_app_secret = secret.trim();
    const r = await run(() => patch('/api/settings', body), { success: 'Saved.' });
    if (r) { setSecret(''); setup.reload(); refresh(); }
  };
  const setEnv = async (env: string) => {
    const r = await run(() => patch('/api/settings', { clover_env: env }), { success: env === 'production' ? 'Clover is on production: real cards from now on. Connect the real merchant account below.' : 'Clover is on the sandbox (test cards).' });
    if (r) { setup.reload(); refresh(); }
  };
  const connect = async () => {
    const r = await run(() => post('/api/clover/connect'));
    if (r?.url) window.location.assign(r.url);
  };
  const disconnect = async () => {
    const r = await run(() => post('/api/clover/disconnect'), { success: 'Clover disconnected.' });
    if (r) { setup.reload(); refresh(); }
  };
  const saveSerial = async (st: any) => {
    const r = await run(() => patch(`/api/clover/devices/${st.id}`, { serial: serials[st.id] ?? '' }), { success: `${st.name}’s Clover Mini saved.` });
    if (r) { setup.reload(); refresh(); }
  };
  const test = async (st: any) => {
    setTesting(st.id);
    try {
      const r = await post('/api/clover/test', { locationId: st.id });
      setTestResult({ ...testResult, [st.id]: r.connected ? 'Connected: the Mini shows "STORE OS is connected".' : 'Clover can’t reach that Mini. Check it’s on, on Wi-Fi, with Cloud Pay Display started.' });
    } catch (e) {
      setTestResult({ ...testResult, [st.id]: e instanceof Error ? e.message : 'The test failed.' });
    } finally {
      setTesting(null);
    }
  };
  const statusWord = (t: any) => (t.status === 'approved' ? (t.kind === 'refund' ? 'Refunded' : 'Approved') : t.status === 'started' ? 'Waiting' : t.status === 'canceled' ? 'Canceled' : 'Failed');
  return (
    <div className="stack">
      <div className="card stack-sm">
        <div className="spread"><h2>Clover account</h2>{d.connected ? <Badge tone="green" icon="check">Connected{d.env === 'sandbox' ? ' (sandbox)' : ''}</Badge> : <Badge tone="amber">Not connected</Badge>}</div>
        <p className="small muted">STORE OS sends each card sale to the store’s Clover Mini: the customer taps, and the approval lands on the receipt. Set it up once here; then the Card button at checkout has <strong>Send to the Clover</strong>.</p>
        <div className="field">
          <span>Where</span>
          <div className="chips" role="group" aria-label="Clover environment">
            <button type="button" className="chip" aria-pressed={d.env === 'sandbox'} disabled={busy} onClick={() => d.env !== 'sandbox' && setEnv('sandbox')}>Sandbox (testing)</button>
            <button type="button" className="chip" aria-pressed={d.env === 'production'} disabled={busy} onClick={() => d.env !== 'production' && setEnv('production')}>Production (real cards)</button>
          </div>
        </div>
        <div className="form-grid">
          <Field label="App ID" hint={d.app_id_env ? 'Using CLOVER_APP_ID from Render' : 'Clover developer dashboard → STORE OS → App Settings'}>
            <input className="input" value={appId} onChange={(e) => setAppId(e.target.value)} placeholder={d.app_id_env ? d.app_id : 'HRJ241SQ05FB6'} />
          </Field>
          <Field label="App Secret" hint={d.app_secret_env ? 'Using CLOVER_APP_SECRET from Render' : d.app_secret_set ? 'Saved. Paste a new one to replace it' : 'App Settings → App Secret (the eye icon)'}>
            <input className="input" type="password" autoComplete="off" value={secret} onChange={(e) => setSecret(e.target.value)} placeholder={d.app_secret_set ? '••••••••' : 'Paste the secret'} />
          </Field>
        </div>
        <div className="row-wrap">
          <Button variant="primary" busy={busy} onClick={saveApp}>Save</Button>
          {d.configured ? <Button icon="send" busy={busy} onClick={connect}>{d.connected ? 'Connect again' : 'Connect Clover'}</Button> : null}
          {d.connected ? <Button variant="ghost" busy={busy} onClick={disconnect}>Disconnect</Button> : null}
        </div>
        {d.connected ? (
          <dl className="kv">
            <dt>Merchant</dt><dd>{d.merchant_id}</dd>
            <dt>Connected</dt><dd>{d.connected_at ? formatDateTime(d.connected_at) : '—'}</dd>
          </dl>
        ) : (
          <p className="small muted">{d.configured ? 'Tap Connect Clover: it opens Clover’s sign-in. Sign in as the merchant (the sandbox test merchant for testing), and you come back here connected.' : 'Save the App ID and App Secret first.'}</p>
        )}
        {!d.redirect_url ? <div className="banner warn"><Icon.alert /><div className="small">Set <strong>PUBLIC_URL</strong> in Render (this app’s https address) so Clover can send the sign-in back to STORE OS.</div></div> : (
          <p className="small muted">In the Clover developer dashboard, the app’s REST Configuration needs Site URL <strong>{d.redirect_url.replace('/api/clover/callback', '')}</strong> and Alternate Launch Path <strong>/api/clover/callback</strong>.</p>
        )}
      </div>
      <div className="card stack-sm">
        <h2>Each store’s Clover Mini</h2>
        <p className="small muted">The serial number is on the back of the Mini and in its Setup app → Devices. On the Mini, install <strong>Cloud Pay Display</strong> (More Tools → search “cloud” → Connect), open it and tap Start: that is what lets STORE OS send payments to it.</p>
        {d.stores.map((st: any) => (
          <div key={st.id} className="stack-sm" style={{ paddingTop: 8 }}>
            <div className="form-grid">
              <Field label={`${st.name} — Mini serial number`}>
                <input className="input" value={serials[st.id] ?? ''} onChange={(e) => setSerials({ ...serials, [st.id]: e.target.value.toUpperCase() })} placeholder="C032UQ12345678" />
              </Field>
              <div className="row-wrap" style={{ alignItems: 'flex-end' }}>
                <Button busy={busy} onClick={() => saveSerial(st)} disabled={(serials[st.id] ?? '') === st.serial}>Save</Button>
                <Button variant="ghost" busy={testing === st.id} disabled={!d.connected || !st.serial} onClick={() => test(st)}>Test the Mini</Button>
              </div>
            </div>
            {testResult[st.id] ? <p className="small">{testResult[st.id]}</p> : null}
          </div>
        ))}
      </div>
      <div className="card stack-sm">
        <div className="spread"><h2>Latest Clover payments</h2><Button variant="ghost" icon="refresh" onClick={setup.reload}>Refresh</Button></div>
        {d.recent.length ? (
          <div className="list">
            {d.recent.map((t: any) => (
              <div key={t.id} className="list-row" style={{ cursor: 'default' }}>
                <span className="main-col">
                  <span className="t" style={{ display: 'block' }}>{t.kind === 'refund' ? 'Refund' : 'Sale'} {formatCents(t.amount_cents)}{t.card_brand || t.card_last4 ? ` · ${t.card_brand ?? 'Card'}${t.card_last4 ? ` ••••${t.card_last4}` : ''}` : ''}{t.receipt_no ? ` · receipt #${t.receipt_no}` : ''}</span>
                  <span className="s" style={{ display: 'block' }}>{formatDateTime(t.created_at)}{t.employee_name ? ` · ${t.employee_name}` : ''}{t.error ? ` · ${t.error}` : ''}</span>
                </span>
                <Badge tone={t.status === 'approved' ? 'green' : t.status === 'started' ? 'blue' : t.status === 'canceled' ? 'plain' : 'red'}>{statusWord(t)}</Badge>
              </div>
            ))}
          </div>
        ) : <p className="small muted">Nothing sent to a Clover yet.</p>}
      </div>
    </div>
  );
}

/** Points program settings, which categories earn, and the Shopify connection. */
function PointsTab() {
  const s = useLoad(() => get('/api/settings'), []);
  const cats = useLoad(() => get('/api/categories'), []);
  const plan = usePlan();
  const shop = useLoad(() => (plan.home ? get('/api/shopify/status') : Promise.resolve(null)), [plan.home]);
  const { refresh } = useSession();
  const { run, busy } = useAction();
  const [f, setF] = useState<Record<string, string>>({});
  useEffect(() => { if (s.data) setF(s.data); }, [s.data]);
  if (!s.data) return <Spinner />;
  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement>) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    const keys = ['points_per_dollar', 'points_redeem_cents', 'points_min_redeem', 'points_manager_over'];
    const changed = Object.fromEntries(keys.filter((k) => s.data[k] !== f[k]).map((k) => [k, f[k]]));
    const r = await run(() => patch('/api/settings', changed), { success: 'Saved.' });
    if (r) { s.reload(); refresh(); }
  };
  const toggleCat = async (c: any) => {
    const r = await run(() => patch(`/api/categories/${c.id}`, { earnsPoints: !c.earns_points }));
    if (r) cats.setData(r);
  };
  const minPts = Number(f.points_min_redeem || 0);
  const cpp = Number(f.points_redeem_cents || 1);
  const sh = shop.data;
  return (
    <div className="stack">
      <div className="card stack">
        <h2>Points</h2>
        <div className="form-grid">
          <Field label="Points per $1 spent" hint="on what they pay before tax"><input className="input" inputMode="decimal" value={f.points_per_dollar ?? ''} onChange={set('points_per_dollar')} /></Field>
          <Field label="Cents off per point" hint={`${minPts.toLocaleString('en-US')} points = $${((minPts * cpp) / 100).toFixed(2)} off`}><input className="input" inputMode="numeric" value={f.points_redeem_cents ?? ''} onChange={set('points_redeem_cents')} /></Field>
          <Field label="Smallest number of points to use"><input className="input" inputMode="numeric" value={f.points_min_redeem ?? ''} onChange={set('points_min_redeem')} /></Field>
          <Field label="Manager code needed above (points)"><input className="input" inputMode="numeric" value={f.points_manager_over ?? ''} onChange={set('points_manager_over')} /></Field>
        </div>
        <div><Button variant="primary" busy={busy} onClick={save}>Save points settings</Button></div>
      </div>
      <div className="card stack-sm">
        <h2>Which items earn points</h2>
        <p className="small muted">Turn a category off and everything in it (and its sub-categories) stops earning. Repairs always earn.</p>
        {cats.data ? (
          <div className="list">
            {cats.data.map((c: any) => (
              <label key={c.id} className="list-row" style={{ cursor: 'pointer', paddingLeft: c.parent_id ? 36 : undefined }}>
                <span className="main-col t">{c.name}</span>
                <input type="checkbox" checked={!!c.earns_points} onChange={() => toggleCat(c)} aria-label={`${c.name} earns points`} />
              </label>
            ))}
          </div>
        ) : <Spinner />}
      </div>
      {plan.home ? <div className="card stack-sm">
        <div className="spread"><h2>Shopify (website)</h2>{sh ? (sh.connected ? <Badge tone="green" icon="check">Connected</Badge> : <Badge tone="amber">Shopify not connected</Badge>) : null}</div>
        {!sh ? <Spinner /> : sh.connected ? (
          <>
            <dl className="kv">
              <dt>Store</dt><dd>{sh.shop}</dd>
              <dt>Customers linked</dt><dd>{sh.linked_customers}</dd>
              <dt>Waiting to send</dt><dd>{sh.waiting}</dd>
              <dt>Order webhooks</dt><dd className="small">{sh.webhook_url ?? 'Set PUBLIC_URL so Shopify can reach STORE OS'}</dd>
            </dl>
            <div className="row-wrap">
              <Button busy={busy} onClick={async () => { const r = await run(() => post('/api/shopify/sync-all'), { success: 'Queued every customer for Shopify.' }); if (r) shop.reload(); }}>Send all customers to Shopify</Button>
              <Button variant="ghost" icon="refresh" onClick={shop.reload}>Refresh</Button>
            </div>
            {sh.failed.length ? (
              <>
                <h3>Failed syncs</h3>
                <div className="list">
                  {sh.failed.map((j: any) => (
                    <div key={j.id} className="list-row" style={{ cursor: 'default' }}>
                      <span className="main-col"><span className="t" style={{ display: 'block' }}>{j.customer_name || j.kind}</span><span className="s" style={{ display: 'block' }}>{j.last_error} · {j.attempts} tries · {formatDateTime(j.updated_at)}</span></span>
                      <Button size="sm" onClick={async () => { const r = await run(() => post(`/api/shopify/retry/${j.id}`)); if (r !== undefined) shop.reload(); }}>Try again</Button>
                    </div>
                  ))}
                </div>
              </>
            ) : <p className="small muted">No failed syncs.</p>}
            {sh.retrying.length ? <p className="small muted">{sh.retrying.length} waiting to try again (Shopify was slow or down).</p> : null}
            {sh.recent_webhooks.length ? (
              <>
                <h3>Latest website orders</h3>
                {sh.recent_webhooks.map((w: any) => <div key={w.webhook_id} className="spread small"><span>{formatDateTime(w.received_at)} · {w.topic}</span><span className="muted">{w.note || w.status}</span></div>)}
              </>
            ) : null}
          </>
        ) : (
          <div className="stack-sm">
            <p>Points work in the store without Shopify. To share one balance with the website, add these in Render, under Environment for this service:</p>
            <ul className="small">
              <li><strong>SHOPIFY_SHOP</strong>: your-store.myshopify.com</li>
              <li><strong>SHOPIFY_CLIENT_ID</strong> and <strong>SHOPIFY_CLIENT_SECRET</strong>: from the Dev Dashboard app installed on the store</li>
            </ul>
            <p className="small muted">The steps are in the README. Never paste keys into chat or the code.</p>
          </div>
        )}
      </div> : null}
    </div>
  );
}

/** Buying phones: hold days, the seller's statement, the SICKW IMEI checks and the fingerprint scanner. */
function BuyTab() {
  const s = useLoad(() => get('/api/settings'), []);
  const stores = useLoad<{ id: string; name: string }[]>(() => get('/api/locations'), []);
  const { refresh } = useSession();
  const { run, busy } = useAction();
  const toast = useToast();
  const [f, setF] = useState<Record<string, string>>({});
  const [testing, setTesting] = useState<string | null>(null);
  const [tested, setTested] = useState<Record<string, { ok: boolean; text: string }>>({});
  useEffect(() => { if (s.data) setF(s.data); }, [s.data]);
  if (!s.data) return <Spinner />;
  // Each store can use its own SICKW account (Hayward has its own login); empty uses the main key.
  const storeKey = (id: string) => `sickw_api_key@${id}`;
  // The unlock sites STORE OS orders on (GSM Unlockers, HangUnlock): name, address, username, API key.
  const unlockKey = (slot: string, part: string) => `unlock_api_${slot}_${part}`;
  const unlockKeys = UNLOCK_API_SLOTS.flatMap((slot) => ['name', 'url', 'user', 'key'].map((part) => unlockKey(slot, part)));
  const keys = ['buyback_hold_days', 'buyback_terms', 'sickw_api_key', 'sickw_service_id', 'sickw_service_samsung', 'sickw_service_other', 'fingerprint_license', 'buyback_label_size', ...unlockKeys, ...(stores.data ?? []).map((l) => storeKey(l.id))];
  const changedKeys = () => keys.filter((k) => (s.data[k] ?? '') !== (f[k] ?? ''));
  const save = async (): Promise<boolean> => {
    const changed = Object.fromEntries(changedKeys().map((k) => [k, f[k] ?? '']));
    if (!Object.keys(changed).length) return true;
    const r = await run(() => patch('/api/settings', changed), { success: 'Saved.' });
    if (r) { s.reload(); refresh(); }
    return !!r;
  };
  // Saves first, then asks the site for the account balance (free) to show the key works.
  const test = async (slot: string) => {
    setTesting(slot);
    try {
      if (!(await save())) return;
      const b = await get<{ connected: boolean; balance: string | null; currency: string | null; error: string | null; name: string }>(`/api/unlock/balance${qs({ site: slot })}`);
      const result = !b.connected
        ? { ok: false, text: 'Add the site address, username and API key, then test.' }
        : b.error
          ? { ok: false, text: b.error }
          : { ok: true, text: `Connected. Credit on ${b.name}: ${unlockPriceLabel(b.balance, b.currency)}` };
      setTested((t) => ({ ...t, [slot]: result }));
    } catch (e) {
      toast(e instanceof Error ? e.message : 'The test didn’t work.', 'error');
    } finally {
      setTesting(null);
    }
  };
  const svc = (k: string, label: string, hint: string) => (
    <Field label={label} hint={hint}><input className="input" inputMode="numeric" value={f[k] ?? ''} onChange={(e) => setF({ ...f, [k]: e.target.value })} aria-label={`SICKW ${label}`} /></Field>
  );
  return (
    <div className="card stack">
      <div className="form-grid">
        <Field label="Days to hold a bought phone before selling it" hint="0 = sell it the same day. Changing it also changes phones on hold now. (California secondhand dealers: 30)"><input className="input" inputMode="numeric" value={f.buyback_hold_days ?? ''} onChange={(e) => setF({ ...f, buyback_hold_days: e.target.value })} /></Field>
        <Field label="What the seller signs" className="full"><textarea className="input" rows={4} value={f.buyback_terms ?? ''} onChange={(e) => setF({ ...f, buyback_terms: e.target.value })} /></Field>
        <Field label="Label for the back of the phone" hint="in the print window pick the label printer, this paper size, Margins None and Scale 100%">
          <select className="select" value={f.buyback_label_size ?? 'brother_62x100'} onChange={(e) => setF({ ...f, buyback_label_size: e.target.value })}>
            {Object.entries(BUY_LABEL_SIZES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </Field>
      </div>
      <h3>IMEI check (SICKW)</h3>
      <p className="small muted">With the key set, every phone is checked on SICKW before you buy it, and the screen shows everything SICKW sends back. Buying without a clean check needs a manager. Type the key yourself here; it’s never shown again.</p>
      <p className="small muted">The same key runs Phone check (in the menu and beside every IMEI box), where staff can run any SICKW check: iCloud, carrier, blacklist, Samsung and more. A store with its own SICKW account uses its own key (below).</p>
      <div className="form-grid">
        <Field label="SICKW API key" className="full" hint={s.data.sickw_api_key_env === '1' ? 'set in Render (SICKW_API_KEY)' : undefined}><input className="input" value={f.sickw_api_key ?? ''} onChange={(e) => setF({ ...f, sickw_api_key: e.target.value })} autoComplete="off" placeholder={s.data.sickw_api_key_env === '1' ? 'using the key in Render' : 'not set'} /></Field>
        {svc('sickw_service_id', 'iPhone / Apple check', '61 = iPhone Carrier & FMI & Blacklist')}
        {svc('sickw_service_samsung', 'Samsung check', '80 = Samsung Info, 54 = Blacklist')}
        {svc('sickw_service_other', 'Google and other phones', '54 = Blacklist, 42 = Pixel Info')}
      </div>
      <p className="small muted">These are SICKW service numbers (sickw.com → Services). Put up to 3, separated by commas; each one is charged by SICKW. The same phone checked again the same day isn’t charged twice.</p>
      {(stores.data ?? []).length > 1 ? (
        <>
          <h3>A store’s own SICKW account</h3>
          <p className="small muted">A store with its own SICKW login (like Hayward) runs its IMEI checks and Phone check on its own key, paid from its own SICKW balance. A store left empty uses the main key above. Type each key yourself; it’s never shown again.</p>
          <div className="form-grid">
            {(stores.data ?? []).map((l) => (
              <Field key={l.id} label={`${l.name} SICKW key`} hint="optional">
                <input className="input" value={f[storeKey(l.id)] ?? ''} onChange={(e) => setF({ ...f, [storeKey(l.id)]: e.target.value })} autoComplete="off" placeholder="uses the main key" aria-label={`${l.name} SICKW API key`} />
              </Field>
            ))}
          </div>
        </>
      ) : null}
      <h3>Order unlocks in STORE OS</h3>
      <p className="small muted">With your username and API key from each site, staff order unlocks in Phone check (and on Unlock, reset or software) without leaving STORE OS, and the code shows there when it’s ready. Each order is paid from your credit on that site, and STORE OS shows who ordered it.</p>
      <p className="small muted">The key is on each site’s API page (in your account menu, often called API Access). If a site doesn’t show one, ask them to turn on API access for your account. Type the key yourself here; it’s never shown again.</p>
      {UNLOCK_API_SLOTS.map((slot) => {
        const name = f[unlockKey(slot, 'name')] || UNLOCK_API_DEFAULTS[slot].name;
        const t = tested[slot];
        return (
          <div key={slot} className="form-grid">
            <Field label="Site name"><input className="input" value={f[unlockKey(slot, 'name')] ?? ''} onChange={(e) => setF({ ...f, [unlockKey(slot, 'name')]: e.target.value })} maxLength={30} aria-label={`Unlock site ${slot} name`} /></Field>
            <Field label={`${name} address`} hint={`like ${UNLOCK_API_DEFAULTS[slot].url}`}><input className="input" value={f[unlockKey(slot, 'url')] ?? ''} onChange={(e) => setF({ ...f, [unlockKey(slot, 'url')]: e.target.value })} autoComplete="off" inputMode="url" placeholder={UNLOCK_API_DEFAULTS[slot].url} aria-label={`${name} address`} /></Field>
            <Field label={`${name} username`} hint="the one you sign in to the site with"><input className="input" value={f[unlockKey(slot, 'user')] ?? ''} onChange={(e) => setF({ ...f, [unlockKey(slot, 'user')]: e.target.value })} autoComplete="off" autoCapitalize="none" spellCheck={false} placeholder="not set" aria-label={`${name} username`} /></Field>
            <Field label={`${name} API key`}><input className="input" value={f[unlockKey(slot, 'key')] ?? ''} onChange={(e) => setF({ ...f, [unlockKey(slot, 'key')]: e.target.value })} autoComplete="off" autoCapitalize="none" spellCheck={false} placeholder="not set" aria-label={`${name} API key`} /></Field>
            <div className="full unlock-test">
              <Button size="sm" busy={testing === slot} onClick={() => test(slot)}>{`Save and test ${name}`}</Button>
              {t ? <span className={`small ${t.ok ? 'strong' : 'error-text'}`}>{t.text}</span> : null}
            </div>
          </div>
        );
      })}
      <h3>Fingerprint scanner</h3>
      <p className="small muted">Scan thumbprint works with SecuGen scanners (like the Hamster Pro 20): install SecuGen’s free WebAPI app on the counter computer and plug the scanner in. Their app runs 60 days without a license; when you buy one from SecuGen for this website, paste it here. Any other scanner works with Upload scan.</p>
      <div className="form-grid">
        <Field label="SecuGen license for this website" className="full"><input className="input" value={f.fingerprint_license ?? ''} onChange={(e) => setF({ ...f, fingerprint_license: e.target.value })} autoComplete="off" placeholder="not needed for the first 60 days" /></Field>
      </div>
      <div><Button variant="primary" busy={busy} onClick={() => { void save(); }}>Save</Button></div>
    </div>
  );
}

/** Special orders: live package tracking, the PHORA store link, the phone sticker size and the receipt note. */
function OrdersTab() {
  const plan = usePlan();
  const s = useLoad(() => get('/api/settings'), []);
  const setup = useLoad(() => (plan.home ? get('/api/tracking/setup') : Promise.resolve(null)), [plan.home]);
  const { run, busy } = useAction();
  const [f, setF] = useState<Record<string, string>>({});
  useEffect(() => { if (s.data) setF(s.data); }, [s.data]);
  if (!s.data) return <Spinner />;
  const keys = ['easypost_api_key', 'phora_account_email', 'order_label_size', 'order_terms'];
  const save = async () => {
    const changed = Object.fromEntries(keys.filter((k) => s.data[k] !== f[k]).map((k) => [k, (f[k] ?? '').trim()]));
    const r = await run(() => patch('/api/settings', changed), { success: 'Saved.' });
    if (r) { s.reload(); setup.reload(); }
  };
  const st = setup.data;
  const tiers = PHORA_MARKUP_TIERS.map((t, i) => {
    const from = i === 0 ? 0 : PHORA_MARKUP_TIERS[i - 1].underCents;
    const band = Number.isFinite(t.underCents) ? `$${(from / 100).toFixed(0)}–${((t.underCents - 1) / 100).toFixed(2)}` : `$${(from / 100).toFixed(0)}+`;
    return `${band} +$${(t.addCents / 100).toFixed(0)}`;
  }).join(' · ');
  return (
    <div className="stack">
      {plan.home ? <>
      <div className="card stack">
        <div className="spread"><h2>Live tracking</h2>{st ? (st.live_tracking ? <Badge tone="green" icon="check">{st.test_key ? 'On (test key)' : 'On'}</Badge> : <Badge tone="amber">Off</Badge>) : null}</div>
        <p className="small muted">With an EasyPost key, every tracking number (UPS, FedEx, USPS and more) updates by itself: where the package is and the day it should arrive. EasyPost charges about 1 to 3 cents per package. Make a free account at easypost.com, copy the production API key and type it here yourself; it’s never shown again.</p>
        <div className="form-grid">
          <Field label="EasyPost API key"><input className="input" value={f.easypost_api_key ?? ''} onChange={(e) => setF({ ...f, easypost_api_key: e.target.value })} autoComplete="off" placeholder={st?.live_tracking_from === 'environment' ? 'set in Render' : 'not set'} /></Field>
        </div>
      </div>
      <div className="card stack">
        <div className="spread"><h2>PHORA orders</h2>{st ? (st.phora ? <Badge tone="green" icon="check">Connected · {st.phora.shop}</Badge> : <Badge tone="amber">PHORA store not connected</Badge>) : null}</div>
        <p className="small muted">When a part is ordered on phorascreenparts.com, STORE OS reads the PHORA order and fills in the tracking number and arrival date by itself. Type the PHORA order # on the order, or set the email you order with below and orders are matched by the part’s SKU.</p>
        {st && !st.phora ? (
          <div className="stack-sm">
            <p className="small">To connect, add these in Render, under Environment for this service (never in chat or the code):</p>
            <ul className="small">
              <li><strong>PHORA_SHOPIFY_SHOP</strong>: the PHORA store’s .myshopify.com address</li>
              <li><strong>PHORA_SHOPIFY_CLIENT_ID</strong> and <strong>PHORA_SHOPIFY_CLIENT_SECRET</strong>: from a Dev Dashboard app installed on the PHORA store with the read_orders and read_merchant_managed_fulfillment_orders scopes</li>
            </ul>
          </div>
        ) : null}
        <div className="form-grid">
          <Field label="Email you order with on PHORA" hint="optional, for matching orders by SKU"><input className="input" inputMode="email" value={f.phora_account_email ?? ''} onChange={(e) => setF({ ...f, phora_account_email: e.target.value })} placeholder="orders@…" /></Field>
        </div>
      </div>
      </> : null}
      <div className="card stack">
        <h2>Printing</h2>
        <div className="form-grid">
          <Field label="Sticker for the back of the phone" hint="in the print window pick the label printer and this label size, margins None">
            <select className="select" value={f.order_label_size ?? 'brother_62x100'} onChange={(e) => setF({ ...f, order_label_size: e.target.value })}>
              {Object.entries(ORDER_LABEL_SIZES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </Field>
          <Field label="Note on the customer’s order receipt" className="full"><textarea className="input" rows={3} value={f.order_terms ?? ''} onChange={(e) => setF({ ...f, order_terms: e.target.value })} /></Field>
        </div>
        {plan.home ? <p className="small muted">Customer’s price for a part picked from the search: the PHORA website price. Parts PHORA doesn’t list use PHORA’s rule: PhoneLCDParts price × 1.29; MobileSentrix and Kracked Screens price plus {tiers}. Staff can still change the price on each order.</p> : <p className="small muted">Type each part’s price on the order; the tracking number and arrival date are typed in when the part ships.</p>}
      </div>
      <div><Button variant="primary" busy={busy} onClick={save}>Save special order settings</Button></div>
    </div>
  );
}

/** Phone agent voices. ElevenLabs sounds the most like a real person on the phone. */
const VOICES: [string, string][] = [
  ['ElevenLabs:UgBBYS2sOqTuMpoF3BR0', 'ElevenLabs · natural (most human)'],
  ['Google:en-US-Journey-O', 'Google · Journey O (woman)'],
  ['Google:en-US-Journey-D', 'Google · Journey D (man)'],
  ['Amazon:Joanna-Neural', 'Amazon · Joanna (woman)'],
  ['Amazon:Matthew-Neural', 'Amazon · Matthew (man)'],
];

/** Messages & calls: Twilio, store hours, the automatic texts and the after-hours assistant. */
function MessagesTab() {
  const s = useLoad(() => get('/api/settings'), []);
  const setup = useLoad(() => get('/api/messages/setup'), []);
  const { refresh } = useSession();
  const { run, busy } = useAction();
  const [f, setF] = useState<Record<string, string>>({});
  const [testTo, setTestTo] = useState('');
  useEffect(() => { if (s.data) setF(s.data); }, [s.data]);
  if (!s.data) return <Spinner />;
  const keys = [
    'twilio_account_sid', 'twilio_auth_token', 'twilio_from_number', 'twilio_messaging_service_sid', 'call_forward_number', 'call_ring_seconds',
    'messaging_hours', 'missed_call_text', 'repair_ready_text', 'after_hours_text', 'sms_consent_text', 'appointment_reminders', 'quick_replies',
    'pickup_thanks_enabled', 'pickup_thanks_minutes', 'pickup_thanks_text',
    'ai_reply_mode', 'anthropic_api_key', 'ai_model', 'ai_notes', 'ai_max_replies', 'takeover_hours',
    'voice_agent_enabled', 'voice_agent_mode', 'voice_agent_voice', 'voice_agent_greeting', 'voice_agent_max_minutes', 'call_recording',
    'sophia_mode', 'sophia_test_numbers', 'voice_transfer_number', 'sophia_name', 'sophia_voice', 'sophia_voice_model', 'sophia_voice_speed',
    'sophia_voice_stability', 'sophia_voice_similarity', 'sophia_personality', 'sophia_greetings', 'sophia_goodbyes', 'sophia_hearing', 'sophia_arabic_code',
  ];
  const save = async () => {
    const changed = Object.fromEntries(keys.filter((k) => s.data[k] !== f[k]).map((k) => [k, k.endsWith('_text') || k === 'ai_notes' || k === 'messaging_hours' || k === 'voice_agent_greeting' ? (f[k] ?? '') : (f[k] ?? '').trim()]));
    const r = await run(() => patch('/api/settings', changed), { success: 'Saved.' });
    if (r) { s.reload(); setup.reload(); refresh(); }
  };
  const input = (k: string, label: string, hint?: string, props: Record<string, unknown> = {}) => (
    <Field label={label} hint={hint}><input className="input" value={f[k] ?? ''} onChange={(e) => setF({ ...f, [k]: e.target.value })} autoComplete="off" {...props} /></Field>
  );
  const area = (k: string, label: string, hint?: string, rows = 3) => (
    <Field label={label} hint={hint} className="full"><textarea className="input" rows={rows} value={f[k] ?? ''} onChange={(e) => setF({ ...f, [k]: e.target.value })} /></Field>
  );
  const st = setup.data;
  return (
    <div className="stack">
      <div className="card stack">
        <div className="spread"><h2>Twilio</h2>{st ? (st.connected ? <Badge tone="green" icon="check">Connected{st.from_number ? ` · ${formatPhone(st.from_number)}` : ''}</Badge> : <Badge tone="amber">Not connected</Badge>) : null}</div>
        <p className="small muted">Texts and calls go through your Twilio account and phone number. Until it’s connected, texts are saved but not sent. The safest place for the Auth Token is Render → Environment (TWILIO_AUTH_TOKEN); if you type it here it’s never shown again.</p>
        <div className="form-grid">
          {input('twilio_account_sid', 'Account SID', 'starts with AC', { placeholder: st?.env.account ? 'set in Render' : 'AC…' })}
          {input('twilio_auth_token', 'Auth Token', undefined, { placeholder: s.data.twilio_env === '1' ? 'set in Render' : 'not set' })}
          {input('twilio_from_number', 'Store texting number', 'your Twilio number', { inputMode: 'tel', placeholder: st?.env.number ? 'set in Render' : '(510) 555-0100' })}
          {input('twilio_messaging_service_sid', 'Messaging Service SID', 'optional, starts with MG (needed for A2P 10DLC)')}
        </div>
        {st?.webhooks ? (
          <div className="stack-sm setup-links">
            <p className="small">In Twilio → Phone Numbers → your number, set these (HTTP POST):</p>
            <dl className="kv small">
              <dt>A message comes in</dt><dd><code>{st.webhooks.sms}</code></dd>
              <dt>A call comes in</dt><dd><code>{st.webhooks.voice}</code></dd>
            </dl>
            <p className="tiny muted">Delivery reports come back to <code>{st.webhooks.status}</code> by themselves.</p>
          </div>
        ) : st ? <p className="small muted">Set PUBLIC_URL in Render to this app’s address (like https://store-os.onrender.com) to see the links to paste into Twilio.</p> : null}
        {st?.connected ? (
          <div className="row-wrap">
            <input className="input" style={{ maxWidth: 220 }} inputMode="tel" placeholder="Your cell number" value={testTo} onChange={(e) => setTestTo(e.target.value)} aria-label="Send a test text to" />
            <Button busy={busy} disabled={testTo.replace(/\D/g, '').length < 10} onClick={() => run(() => post('/api/messages/test', { to: testTo }), { success: 'Test text sent.' })}>Send a test text</Button>
          </div>
        ) : null}
      </div>

      <div className="card stack">
        <div className="spread"><h2>Hours and calls</h2>{st?.hours ? ('error' in st.hours ? <Badge tone="red">Hours need fixing</Badge> : <Badge tone={st.hours.open_now ? 'green' : 'plain'}>{st.hours.open_now ? 'Open now' : `Closed · opens ${st.hours.next_open ?? '—'}`}</Badge>) : null}</div>
        <div className="form-grid">
          {area('messaging_hours', 'Store hours', 'one line per day or days, like "Mon-Sat 10:00-19:00" or "Sun closed"', 3)}
          {input('call_forward_number', 'Ring this phone for calls', 'the store phone; empty = every call gets the missed-call text', { inputMode: 'tel' })}
          {input('call_ring_seconds', 'Ring for (seconds)', 'then the caller gets the missed-call text', { inputMode: 'numeric' })}
        </div>
        {st?.hours && !('error' in st.hours) ? <p className="small muted">Reads as: {st.hours.text}. Outside these hours calls aren’t forwarded; callers hear that you’re closed and get the missed-call text.</p> : null}
      </div>

      <div className="card stack">
        <h2>Automatic texts</h2>
        <p className="small muted">Blanks you can use: {'{store}'} everywhere; {'{first} {device} {location} {ticket} {due} {phone}'} in the ready text; {'{first} {device} {location} {ticket} {phone}'} in the thank-you text; {'{opens}'} in the after-hours text.</p>
        <div className="form-grid">
          {area('missed_call_text', 'Missed call')}
          {area('repair_ready_text', 'Repair ready', 'sent when a ticket is marked ready (if "Text updates" is on)')}
          {area('after_hours_text', 'After hours', 'when the assistant is off or has no key; once per 12 hours per customer')}
          {area('sms_consent_text', 'Texting box at checkout', 'what the customer agrees to; keep "Reply STOP to opt out"')}
        </div>
        <label className="check"><input type="checkbox" checked={f.pickup_thanks_enabled !== '0'} onChange={(e) => setF({ ...f, pickup_thanks_enabled: e.target.checked ? '1' : '0' })} /> Text a thank-you after a repair is picked up</label>
        <div className="form-grid">
          {input('pickup_thanks_minutes', 'Minutes after pickup', '5 to 10 is about right', { inputMode: 'numeric' })}
          {area('pickup_thanks_text', 'Thank-you text', 'a thank-you from the owner and a nudge for a Google or Yelp review; emojis make each text cost more parts', 9)}
        </div>
        <p className="small muted">Goes out {f.pickup_thanks_minutes || '7'} minutes after the ticket closes as picked up (the Picked up button, or paying for a ready repair), only to customers who agreed to texts ("Text updates" on the ticket, or the box at checkout), once a month per customer. Old RepairDesk tickets closed in bulk never get one.</p>
        <label className="check"><input type="checkbox" checked={f.appointment_reminders !== '0'} onChange={(e) => setF({ ...f, appointment_reminders: e.target.checked ? '1' : '0' })} /> Text a reminder before each booked visit</label>
        <p className="small muted">About a day before the visit (between 9 AM and 8 PM), with the confirmation number. Visits staff book get one only if the customer agreed to texts.</p>
        <p className="small muted">STOP, START and HELP are handled by Twilio: a customer who replies STOP gets no more texts from STORE OS until they reply START.</p>
      </div>

      <div className="card stack">
        <h2>Quick replies</h2>
        <p className="small muted">One-tap answers above the reply box in Messages. Tapping one puts it in the box for staff to change and send; nothing is sent by itself. {'{first}'} becomes the customer’s first name.</p>
        <div className="form-grid">
          {area('quick_replies', 'Quick replies', 'one per line, up to 12', 6)}
        </div>
      </div>

      <div className="card stack">
        <div className="spread"><h2>After-hours assistant</h2>{st ? (st.assistant.key ? <Badge tone="green" icon="spark">Ready</Badge> : <Badge tone="amber">No Claude key</Badge>) : null}</div>
        <p className="small muted">The assistant answers texts using only STORE OS: the customer’s own repair tickets and special orders (by the number that texted), the website’s repair prices and what’s in stock. It never makes up prices, and hands anything else to staff (marked “Needs a reply”). Once someone answers a conversation, the assistant stays out of it.</p>
        <div className="form-grid">
          <Field label="When it answers">
            <select className="select" value={f.ai_reply_mode ?? 'after_hours'} onChange={(e) => setF({ ...f, ai_reply_mode: e.target.value })}>
              <option value="after_hours">Only when the store is closed</option>
              <option value="always">Any time nobody has taken over</option>
              <option value="off">Never (closed-hours text only)</option>
            </select>
          </Field>
          {input('anthropic_api_key', 'Claude API key', 'from console.anthropic.com', { placeholder: s.data.anthropic_api_key_env === '1' ? 'set in Render (ANTHROPIC_API_KEY)' : 'sk-ant-…' })}
          {input('ai_model', 'Claude model', 'claude-haiku-5-5 is fast and low cost')}
          {input('ai_max_replies', 'Most answers per customer in 12 hours', 'then it flags staff', { inputMode: 'numeric' })}
          {input('takeover_hours', 'Hand back to the assistant after (hours)', 'when staff took over and stopped answering', { inputMode: 'numeric' })}
          {area('ai_notes', 'Facts for the assistant', 'prices and services, walk-ins, payment methods, how long repairs take, warranty… one per line (the phone agent uses them too)', 8)}
        </div>
      </div>

      <div className="card stack">
        <div className="spread">
          <h2>Phone agent</h2>
          {st?.phone_agent ? (st.phone_agent.ready ? <Badge tone="green" icon="spark">Answering calls</Badge> : <Badge tone="amber">{!st.assistant.key ? 'No Claude key: missed-call text instead' : f.voice_agent_enabled === '0' ? 'Off' : 'Not ready'}</Badge>) : null}
        </div>
        <p className="small muted">The phone agent answers calls by voice (Twilio ConversationRelay + Claude). It uses only STORE OS: your hours and stores, the website’s repair prices, what’s in stock, the caller’s own repair tickets and special orders (matched by the number they call from) and the facts above. It never makes up a price. It talks like a friendly person at the store, books store visits (they show on the Today screen, and the caller gets a confirmation text) and says honestly that it’s the store’s virtual assistant if someone asks. When it isn’t sure it offers a call back and marks the call “Needs a reply”. If the caller asks for a person while you’re open, it transfers them to the store phone. Every call is in Messages → Calls with the recording, what was said and a short summary.</p>
        <div className="form-grid">
          <label className="check"><input type="checkbox" checked={f.voice_agent_enabled === '1'} onChange={(e) => setF({ ...f, voice_agent_enabled: e.target.checked ? '1' : '0' })} /> Phone agent on</label>
          <label className="check"><input type="checkbox" checked={f.call_recording === '1'} onChange={(e) => setF({ ...f, call_recording: e.target.checked ? '1' : '0' })} /> Record every call</label>
          <Field label="Which calls it answers">
            <select className="select" value={f.voice_agent_mode ?? 'fallback'} onChange={(e) => setF({ ...f, voice_agent_mode: e.target.value })}>
              <option value="fallback">When staff don’t pick up, and after hours</option>
              <option value="no_answer">Only when staff don’t pick up</option>
              <option value="after_hours">Only after hours</option>
              <option value="all">Every call</option>
            </select>
          </Field>
          <Field label="Voice">
            <select className="select" value={f.voice_agent_voice ?? 'Google:en-US-Journey-O'} onChange={(e) => setF({ ...f, voice_agent_voice: e.target.value })}>
              {VOICES.concat(VOICES.some(([v]) => v === f.voice_agent_voice) || !f.voice_agent_voice ? [] : [[f.voice_agent_voice, f.voice_agent_voice]])
                .map(([v, label]) => <option key={v} value={v}>{label}</option>)}
            </select>
          </Field>
          {area('voice_agent_greeting', 'Greeting', 'the first thing callers hear; it has to say the call may be recorded (California). {store} is the business name.', 2)}
          {input('voice_agent_max_minutes', 'Longest call (minutes)', 'then it wraps up and flags a call back', { inputMode: 'numeric' })}
        </div>
        <p className="small muted">When staff are asked first, the store phone rings for {f.call_ring_seconds || 20} seconds (set above) after callers hear the recording notice. Transfers go to “Ring this phone for calls”. Without a Claude key, or with the agent off, calls work as before: ring the store, then the missed-call text.</p>
      </div>

      <SophiaCard f={f} setF={setF} times={st?.answer_times} week={st?.sophia_week} />

      <div><Button variant="primary" busy={busy} onClick={save}>Save messages & calls settings</Button></div>

      <PriceListCard />
      <LearnCard />
      <TryAssistant ready={!!st?.assistant.key} />
    </div>
  );
}

interface LearnItem {
  id: string;
  source: 'call' | 'text' | 'owner';
  question: string;
  asked: number;
  answer: string | null;
  last_asked_at: number;
  resolved_at: number | null;
  resolved_name: string | null;
}

/**
 * What Sophia learns: questions she (or the text assistant) couldn't answer, and the owner's
 * answers, which both use from then on. Nothing is learned without the owner.
 */
function LearnCard() {
  const { run, busy } = useAction();
  const [view, setView] = useState<'new' | 'answered'>('new');
  const waiting = useLoad<LearnItem[]>(() => get('/api/knowledge?status=new'), []);
  const learned = useLoad<LearnItem[]>(() => get('/api/knowledge?status=answered'), []);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  // The question as Sophia will see it: the customer's words, which the owner can tidy up first.
  const [asked, setAsked] = useState<Record<string, string>>({});
  const [adding, setAdding] = useState({ question: '', answer: '' });
  const reload = () => { waiting.reload(); learned.reload(); };
  const count = waiting.data?.length ?? 0;
  const answer = async (g: LearnItem) => {
    const r = await run(() => post(`/api/knowledge/${g.id}/answer`, { answer: (drafts[g.id] ?? '').trim(), question: (asked[g.id] ?? g.question).trim() }), { success: 'Saved. Sophia uses this answer from now on.' });
    if (r) reload();
  };
  const dismiss = async (g: LearnItem) => {
    const r = await run(() => post(`/api/knowledge/${g.id}/dismiss`), { success: g.answer ? 'Removed. Sophia stops using it.' : 'Dismissed.' });
    if (r) reload();
  };
  const add = async () => {
    const r = await run(() => post('/api/knowledge', { question: adding.question.trim(), answer: adding.answer.trim() }), { success: 'Added. Sophia uses this answer from now on.' });
    if (r) { setAdding({ question: '', answer: '' }); reload(); }
  };
  const when = (g: LearnItem) => `${g.source === 'call' ? 'On a call' : g.source === 'text' ? 'By text' : 'Added by you'} · ${formatDateTime(g.last_asked_at)}${g.asked > 1 ? ` · asked ${g.asked} times` : ''}`;
  return (
    <div className="card stack">
      <div className="spread">
        <h2>What Sophia learns</h2>
        {waiting.data ? (count ? <Badge tone="amber">{count} to answer</Badge> : <Badge tone="green" icon="check">Nothing waiting</Badge>) : null}
      </div>
      <p className="small muted">When Sophia or the text assistant can’t answer a question, they offer a call back (or hand the text to staff) and the question shows up here. Write the answer once and both use it from then on. Nothing is learned without you.</p>
      <Tabs label="What Sophia learns" value={view} onChange={setView} tabs={[{ id: 'new', label: `To answer${count ? ` (${count})` : ''}` }, { id: 'answered', label: `Learned${learned.data?.length ? ` (${learned.data.length})` : ''}` }]} />
      {view === 'new' ? (
        waiting.data?.length ? (
          <div className="stack-sm">
            {waiting.data.map((g) => (
              <div key={g.id} className="learn-item">
                <div className="strong">“{g.question}”</div>
                <div className="tiny muted">{when(g)}</div>
                <input className="input" maxLength={300} value={asked[g.id] ?? g.question} onChange={(e) => setAsked({ ...asked, [g.id]: e.target.value })} aria-label={`Question as Sophia will see it: ${g.question}`} title="The question as Sophia will see it. Tidy it up if you like." />
                <textarea className="input" rows={2} maxLength={600} value={drafts[g.id] ?? ''} onChange={(e) => setDrafts({ ...drafts, [g.id]: e.target.value })} placeholder="The answer Sophia should give" aria-label={`Answer to ${g.question}`} />
                <div className="row-wrap">
                  <Button size="sm" variant="primary" busy={busy} disabled={!(drafts[g.id] ?? '').trim() || (asked[g.id] ?? g.question).trim().length < 4} onClick={() => answer(g)}>Save answer</Button>
                  <Button size="sm" busy={busy} onClick={() => dismiss(g)}>Not needed</Button>
                </div>
              </div>
            ))}
          </div>
        ) : waiting.data ? <p className="small muted">No questions waiting. New ones show up here after calls and texts.</p> : <Spinner />
      ) : (
        <div className="stack-sm">
          {learned.data?.length ? learned.data.map((g) => (
            <div key={g.id} className="learn-item">
              <div className="strong">“{g.question}”</div>
              <div className="pc-text">{g.answer}</div>
              <div className="row-wrap">
                <span className="tiny muted grow">{g.resolved_name ? `Answered by ${g.resolved_name}` : 'Answered'}{g.resolved_at ? ` · ${formatDateTime(g.resolved_at)}` : ''}</span>
                <Button size="sm" busy={busy} onClick={() => dismiss(g)}>Remove</Button>
              </div>
            </div>
          )) : learned.data ? <p className="small muted">Nothing learned yet.</p> : <Spinner />}
          <div className="learn-item">
            <div className="small strong">Teach an answer before anyone asks</div>
            <input className="input" maxLength={300} value={adding.question} onChange={(e) => setAdding({ ...adding, question: e.target.value })} placeholder="Question, like: Do you fix water damage?" aria-label="New question" />
            <textarea className="input" rows={2} maxLength={600} value={adding.answer} onChange={(e) => setAdding({ ...adding, answer: e.target.value })} placeholder="Answer, like: Yes. We do a free check first, and repairs start at 79 dollars." aria-label="New answer" />
            <div><Button size="sm" variant="primary" busy={busy} disabled={adding.question.trim().length < 4 || adding.answer.trim().length < 2} onClick={add}>Add</Button></div>
          </div>
        </div>
      )}
    </div>
  );
}

const secs = (ms: number | null | undefined) => (ms == null ? '—' : `${(ms / 1000).toFixed(1)} s`);
/** "1.1 s for half the answers, 2.0 s for 9 in 10 (4 calls)", or that there were none yet. */
const pace = (t: { calls: number; answers: number; p50: number | null; p90: number | null }) =>
  t.answers ? `${secs(t.p50)} for half the answers, ${secs(t.p90)} for 9 in 10 (${t.calls} ${t.calls === 1 ? 'call' : 'calls'})` : 'no answered calls yet';

/**
 * Sophia, the phone receptionist: who gets her (test numbers first), where she transfers callers,
 * her voice, personality, greetings and goodbyes. Saved with the button under the cards.
 */
function SophiaCard({ f, setF, times, week }: { f: Record<string, string>; setF: (f: Record<string, string>) => void; times?: { sophia: any; legacy: any }; week?: { calls: number; booked: number; transferred: number; callbacks: number; minutes: number | null; questions: number } }) {
  const set = (k: string) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  const voices = SOPHIA_VOICES.some((v) => v.id === f.sophia_voice) || !f.sophia_voice ? SOPHIA_VOICES : [...SOPHIA_VOICES, { id: f.sophia_voice, label: f.sophia_voice }];
  const eleven = (f.sophia_voice ?? '').startsWith('ElevenLabs:');
  const mode = f.sophia_mode ?? 'test';
  return (
    <div className="card stack" aria-label="Sophia">
      <div className="spread" style={{ flexWrap: 'wrap', rowGap: 6 }}>
        <h2>{f.sophia_name || 'Sophia'}, the phone receptionist</h2>
        <Badge tone={mode === 'all' ? 'green' : mode === 'test' ? 'blue' : 'plain'} icon="spark">{mode === 'all' ? 'Answering every caller' : mode === 'test' ? 'Test numbers only' : 'Off'}</Badge>
      </div>
      <p className="small muted">Sophia answers the calls the phone agent takes, with her own voice and personality. She starts talking about a sentence in instead of after the whole answer, stops when the caller talks over her (a quick “yeah” or “okay” doesn’t stop her), speaks English, Spanish and Arabic (switching when the caller does), and transfers callers who ask for a person to the transfer phone while the store is open. While she’s on “Test numbers only”, everyone else gets the phone agent as before.</p>
      {week ? (
        <div className="sophia-week" aria-label={`${f.sophia_name || 'Sophia'} this week`}>
          <div><span className="big">{week.calls}</span><span className="small muted">calls this week{week.minutes !== null ? ` · ${week.minutes} min each` : ''}</span></div>
          <div><span className="big">{week.booked}</span><span className="small muted">visits booked</span></div>
          <div><span className="big">{week.transferred}</span><span className="small muted">sent to staff</span></div>
          <div><span className="big">{week.callbacks}</span><span className="small muted">call backs asked for</span></div>
          <div><span className="big">{week.questions}</span><span className="small muted">questions to answer (below)</span></div>
        </div>
      ) : null}
      {times ? (
        <p className="small"><b>How fast callers get an answer this week</b>, from when they stop talking. {f.sophia_name || 'Sophia'}: {pace(times.sophia)}. The agent before her: {pace(times.legacy)}.</p>
      ) : null}
      <div className="form-grid">
        <Field label="Who gets her">
          <select className="select" value={mode} onChange={set('sophia_mode')}>
            <option value="test">Test numbers only</option>
            <option value="all">Every caller</option>
            <option value="off">Nobody (the phone agent as before)</option>
          </select>
        </Field>
        <Field label="Test numbers" hint="your cell first; commas between numbers"><input className="input" value={f.sophia_test_numbers ?? ''} onChange={set('sophia_test_numbers')} inputMode="tel" placeholder="(510) 555-0123" /></Field>
        <Field label="Transfer callers to" hint="a manager’s cell, not the store number that forwards to her"><input className="input" value={f.voice_transfer_number ?? ''} onChange={set('voice_transfer_number')} inputMode="tel" /></Field>
        <Field label="Her name"><input className="input" value={f.sophia_name ?? ''} onChange={set('sophia_name')} maxLength={30} /></Field>
        <Field label="Voice">
          <select className="select" value={f.sophia_voice ?? ''} onChange={set('sophia_voice')}>
            {voices.map((v) => <option key={v.id} value={v.id}>{v.label}</option>)}
          </select>
        </Field>
        {eleven ? (
          <>
            <Field label="Voice quality">
              <select className="select" value={f.sophia_voice_model ?? 'flash_v2_5'} onChange={set('sophia_voice_model')}>
                <option value="flash_v2_5">Flash: fastest replies</option>
                <option value="turbo_v2_5">Turbo: a little richer, a little slower</option>
              </select>
            </Field>
            <Field label="Speed" hint="0.7 to 1.2 · 1.0 is normal"><input className="input" value={f.sophia_voice_speed ?? ''} onChange={set('sophia_voice_speed')} inputMode="decimal" /></Field>
            <Field label="Steadiness" hint="0 to 1 · lower sounds more expressive"><input className="input" value={f.sophia_voice_stability ?? ''} onChange={set('sophia_voice_stability')} inputMode="decimal" /></Field>
            <Field label="Closeness to the voice" hint="0 to 1"><input className="input" value={f.sophia_voice_similarity ?? ''} onChange={set('sophia_voice_similarity')} inputMode="decimal" /></Field>
          </>
        ) : null}
        <Field label="How she hears callers">
          <select className="select" value={f.sophia_hearing ?? 'deepgram_multi'} onChange={set('sophia_hearing')}>
            {Object.entries(SOPHIA_HEARING).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </Field>
        <Field label="Arabic" hint="what she listens for when a caller speaks Arabic">
          <select className="select" value={f.sophia_arabic_code ?? 'ar'} onChange={set('sophia_arabic_code')}>
            {SOPHIA_ARABIC_CODES.map((a) => <option key={a.code} value={a.code}>{a.label}</option>)}
          </select>
        </Field>
        <Field label="Her personality" hint="who she is and how she comes across; {name} and {store} are filled in" className="full"><textarea className="input" rows={6} value={f.sophia_personality ?? ''} onChange={set('sophia_personality')} /></Field>
        <Field label="Greetings" hint="one per line, used in turn; “this call may be recorded” is added when calls are recorded" className="full"><textarea className="input" rows={3} value={f.sophia_greetings ?? ''} onChange={set('sophia_greetings')} /></Field>
        <Field label="Goodbyes" hint="one per line; examples she varies, not a script" className="full"><textarea className="input" rows={4} value={f.sophia_goodbyes ?? ''} onChange={set('sophia_goodbyes')} /></Field>
      </div>
      <p className="small muted">She knows what the phone agent knows: the facts for the assistant above, your hours and stores, repair prices, stock, the repair warranty and policies printed on receipts, and the caller’s own repairs, orders and visits. Try her below without calling.</p>
    </div>
  );
}

/** The repair price list the phone agent and text assistant quote from (the website's export). */
function PriceListCard() {
  const st = useLoad<any>(() => get('/api/price-list'), []);
  const { run, busy } = useAction();
  const [note, setNote] = useState('');
  const upload = async (file: File | undefined) => {
    if (!file) return;
    const csv = await file.text();
    const r = await run(() => post('/api/price-list', { csv }), { success: 'Price list loaded. The assistant uses it right away.' });
    if (r) { st.setData(r); setNote(`${r.prices.toLocaleString('en-US')} prices for ${r.devices} devices loaded from ${file.name}.`); }
  };
  const d = st.data;
  return (
    <section className="card stack" aria-label="Repair price list">
      <div className="spread"><h2>Repair price list</h2>{d ? (d.prices ? <Badge tone="green" icon="check">{d.prices.toLocaleString('en-US')} prices</Badge> : <Badge tone="amber">Not loaded</Badge>) : null}</div>
      <p className="small muted">The phone agent and the text assistant quote repair prices from this list: every phone, iPad, MacBook and console the website prices, found the way customers say it (“iPhone 11”, “S23 Ultra”, “iPad 9th gen”, “PS5”). Upload the price list exported from the website (.csv) again after changing prices. Part costs in the file are never kept.</p>
      {d?.prices ? <p className="small">{d.devices} devices · loaded {d.updated_at ? formatDateTime(d.updated_at) : ''}{d.updated_by ? ` by ${d.updated_by}` : ''}</p> : null}
      <label className="btn btn-secondary file-pick">
        <input type="file" accept=".csv,text/csv" aria-label="Upload the repair price list" disabled={busy} onChange={(e) => { void upload(e.target.files?.[0]); e.target.value = ''; }} />
        {busy ? 'Loading…' : 'Upload price list (.csv)'}
      </label>
      {note ? <p className="small">{note}</p> : null}
    </section>
  );
}

type TryTurn = { role: 'user' | 'assistant'; content: string; used?: string[] };
const TOOL_WORDS: Record<string, string> = {
  repair_prices: 'repair prices', check_stock: 'stock', my_repairs: 'their repairs', my_orders: 'their orders',
  book_appointment: 'booking', my_appointments: 'their visit', cancel_appointment: 'canceling', request_callback: 'call back',
  transfer_to_staff: 'transfer', hand_to_staff: 'staff', end_call: 'hang up',
};

/** Ask the assistant what a customer would, to hear what it says. Nothing is texted, booked or flagged. */
function TryAssistant({ ready }: { ready: boolean }) {
  const [channel, setChannel] = useState<'phone' | 'text' | 'sophia'>('sophia');
  const [turns, setTurns] = useState<TryTurn[]>([]);
  const [q, setQ] = useState('');
  const { run, busy } = useAction();
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => { box.current?.scrollTo({ top: box.current.scrollHeight }); }, [turns]);
  const ask = async () => {
    const text = q.trim();
    if (!text || busy) return;
    const next: TryTurn[] = [...turns, { role: 'user', content: text }];
    setTurns(next);
    setQ('');
    const r = await run(() => post<{ reply: string; used: string[] }>('/api/messages/assistant-try', { channel, messages: next.map(({ role, content }) => ({ role, content })) }));
    if (!r) {
      setTurns(turns);
      setQ(text);
      return;
    }
    setTurns([...next, { role: 'assistant', content: r.reply || '(No answer. A caller would hear “I’ll have someone call you back.”)', used: r.used }]);
  };
  const pick = (c: 'phone' | 'text' | 'sophia') => { setChannel(c); setTurns([]); };
  return (
    <section className="card stack try-ai" aria-label="Try the assistant">
      <div className="spread">
        <h2>Try the assistant</h2>
        <div className="chips" role="group" aria-label="Answer as">
          <button type="button" className="chip" aria-pressed={channel === 'sophia'} onClick={() => pick('sophia')}>Sophia</button>
          <button type="button" className="chip" aria-pressed={channel === 'phone'} onClick={() => pick('phone')}>Phone call</button>
          <button type="button" className="chip" aria-pressed={channel === 'text'} onClick={() => pick('text')}>Text</button>
        </div>
      </div>
      <p className="small muted">Ask what a customer would, like “Do you buy phones?” or “Can I come in tomorrow at noon?”, and see the answer. It uses the saved facts and your real prices and stock. Nothing is texted, booked or flagged.</p>
      {turns.length ? (
        <div className="msg-bubbles try-bubbles" aria-live="polite" ref={box}>
          {turns.map((t, i) => (
            <div key={i} className={t.role === 'user' ? 'bubble in' : 'bubble out ai'}>
              {t.content}
              {t.used?.length ? <div className="tiny try-used">Used: {t.used.map((u) => TOOL_WORDS[u] ?? u).join(', ')}</div> : null}
            </div>
          ))}
        </div>
      ) : null}
      <form className="row-wrap" onSubmit={(e) => { e.preventDefault(); void ask(); }}>
        <input className="input try-input" value={q} onChange={(e) => setQ(e.target.value)} maxLength={600} placeholder={channel === 'text' ? 'What the customer texts…' : 'What the caller says…'} aria-label="What the customer says" disabled={!ready} />
        <Button type="submit" variant="primary" busy={busy} disabled={!ready || !q.trim()}>Ask</Button>
        {turns.length ? <Button onClick={() => { setTurns([]); setQ(''); }}>Start over</Button> : null}
      </form>
      {!ready ? <p className="small muted">Add the Claude API key above first.</p> : null}
    </section>
  );
}

/** Bringing customers and their repairs over from RepairDesk, through RepairDesk's API. */
function ImportTab() {
  const st = useLoad<any>(() => get('/api/import/repairdesk'), []);
  const { run, busy } = useAction();
  const [key, setKey] = useState('');
  const [preview, setPreview] = useState<any>(null);
  const running = !!st.data?.progress?.running;
  useEffect(() => {
    if (!running) return;
    const t = setInterval(() => st.reload(), 2_000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running]);
  if (!st.data) return <Spinner />;
  const p = st.data.progress;
  const saveKey = async () => {
    const r = await run(() => patch('/api/settings', { repairdesk_api_key: key.trim() }), { success: 'Key saved.' });
    if (r) { setKey(''); st.reload(); }
  };
  const look = async () => {
    const r = await run(() => post('/api/import/repairdesk/preview'));
    if (r) setPreview(r);
  };
  const start = async () => {
    const r = await run(() => post('/api/import/repairdesk/start'), { success: 'Started. It keeps going if you leave this page.' });
    if (r) st.reload();
  };
  return (
    <div className="stack">
      <div className="card stack">
        <h2>Bring customers and repairs over from RepairDesk</h2>
        <p className="small muted" style={{ margin: 0 }}>
          Every customer comes over with their phone, email and address (and any store credit), and every repair ticket with its device, IMEI, what was fixed, the price and the status.
          Someone already in STORE OS with the same name and phone (or email) isn’t doubled; they just get what’s missing filled in. Family members who share a phone each get their own record. Running it again only adds what’s new.
          Repairs still open in RepairDesk from the last 60 days go on the repair board; older ones come over as history.
          Every repair keeps its RepairDesk ticket number (T-1262 stays T-1262, as on RepairDesk’s labels), and tickets made in STORE OS keep theirs. A second phone on the same RepairDesk ticket gets the next STORE OS number; searching the RepairDesk number finds both.
        </p>
        <div className="stack-sm">
          <span className="small strong">1. Your RepairDesk API key</span>
          <span className="small muted">In RepairDesk: Store Settings → Other Information → API KEY. Copy it and paste it here yourself.</span>
          <div className="row" style={{ gap: 8 }}>
            <input className="input grow" value={key} onChange={(e) => setKey(e.target.value)} placeholder={st.data.key_set ? 'Saved. Paste a new key to replace it' : 'Paste the API key'} aria-label="RepairDesk API key" autoComplete="off" />
            <Button variant="primary" busy={busy} disabled={key.trim().length < 10} onClick={saveKey}>Save key</Button>
          </div>
          {st.data.key_set ? <Badge tone="green" icon="check">Key saved</Badge> : null}
        </div>
        <div className="stack-sm">
          <span className="small strong">2. Check what’s there</span>
          <div><Button busy={busy} disabled={!st.data.key_set || running} onClick={look}>Look at RepairDesk</Button></div>
          {preview ? (
            <div className="stack-sm">
              <div className="small">{preview.tickets_total ? `${preview.tickets_total.toLocaleString('en-US')} repair tickets in RepairDesk. ` : ''}The first few:</div>
              <div className="list">
                {preview.customers.map((c: any, i: number) => <div key={`c${i}`} className="list-row" style={{ cursor: 'default' }}><span className="main-col"><span className="t" style={{ display: 'block' }}>{c.name || 'No name'}</span><span className="s" style={{ display: 'block' }}>{[c.phone ? formatPhone(c.phone) : null, c.email].filter(Boolean).join(' · ') || 'No phone or email'}</span></span><Badge>Customer</Badge></div>)}
                {preview.repairs.map((r: any, i: number) => <div key={`r${i}`} className="list-row" style={{ cursor: 'default' }}><span className="main-col"><span className="t" style={{ display: 'block' }}>{r.ticket} · {r.device}</span><span className="s" style={{ display: 'block' }}>{[r.customer, r.issue, r.status].filter(Boolean).join(' · ')}</span></span><Badge tone="blue">Repair</Badge></div>)}
              </div>
            </div>
          ) : null}
        </div>
        <div className="stack-sm">
          <span className="small strong">3. Bring everything over</span>
          <div><Button variant="primary" icon="arrowRight" busy={busy} disabled={!st.data.key_set || running} onClick={start}>{st.data.imported.customers ? 'Bring over what’s new' : 'Start the import'}</Button></div>
          {p && p.phase !== 'idle' ? (
            <div className={`banner ${p.phase === 'failed' ? 'bad' : ''}`} role="status">
              <div>
                <div className="strong">{p.phase === 'customers' ? 'Bringing customers over…' : p.phase === 'repairs' ? 'Bringing repairs over…' : p.phase === 'done' ? 'Done.' : 'The import stopped.'}</div>
                <div className="small">
                  Customers: {p.customers.seen.toLocaleString('en-US')} read, {p.customers.added.toLocaleString('en-US')} added, {p.customers.matched.toLocaleString('en-US')} matched to customers already here
                  {p.customers.credit_cents ? ` (store credit brought over: ${formatCents(p.customers.credit_cents)})` : ''}.
                  {' '}Repairs: {p.repairs.seen.toLocaleString('en-US')} read, {p.repairs.added.toLocaleString('en-US')} added ({p.repairs.open.toLocaleString('en-US')} still open){p.repairs.already ? `, ${p.repairs.already.toLocaleString('en-US')} were already here` : ''}.
                  {p.repairs.renumbered ? ` ${p.repairs.renumbered.toLocaleString('en-US')} repairs brought over before now have their RepairDesk ticket numbers.` : ''}
                </div>
                {p.error ? <div className="small">{p.error}</div> : null}
                {p.finished_at ? <div className="small muted">{formatDateTime(p.finished_at)}</div> : null}
              </div>
            </div>
          ) : null}
          <p className="small muted" style={{ margin: 0 }}>In STORE OS now from RepairDesk: {st.data.imported.customers.toLocaleString('en-US')} customers, {st.data.imported.repairs.toLocaleString('en-US')} repairs.</p>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- printers (PrintNode)

interface PnPrinter { id: number; name: string; state: string; computer: { id: number; name: string; state: string }; papers: { name: string; w: number | null; h: number | null }[] }
type PnLabel = { id: number; name: string; computer: string | null; paper?: string | null; rotate?: number };
interface PnStore {
  location_id: string; name: string;
  receipt: { id: number; name: string; computer: string | null; dots?: number } | null;
  label: PnLabel | null;
  /** The price sticker printer (the Rollo, 3 x 2 in). */
  sticker?: PnLabel | null;
  /** The cash drawer opens by itself through the receipt printer (on unless turned off). */
  open_drawer?: boolean;
}

/** The printer's paper closest to a label `w` x `h` mm (either way round), or none when nothing is close. */
function closestPaper(papers: PnPrinter['papers'], w: number, h: number): string {
  const off = (p: PnPrinter['papers'][number]) => (p.w && p.h ? Math.min(Math.abs(p.w - w) + Math.abs(p.h - h), Math.abs(p.w - h) + Math.abs(p.h - w)) : 999);
  const best = [...papers].sort((a, b) => off(a) - off(b))[0];
  return best && off(best) < 12 ? best.name : '';
}
/** The DYMO paper closest to the technician's label, 89 x 29 mm (DYMO 30252 Address, either way round). */
const closestLabelPaper = (papers: PnPrinter['papers']) => closestPaper(papers, 89, 29);
/** The paper closest to the price sticker: 3 x 2 in (76.2 x 50.8 mm), or the 4 x 3 in label (101.6 x 76.2 mm). */
const closestStickerPaper = (papers: PnPrinter['papers'], size: PriceStickerSize) => (size === '4x3' ? closestPaper(papers, 101.6, 76.2) : size === '2x3' ? closestPaper(papers, 50.8, 76.2) : size === '2x2' ? closestPaper(papers, 50.8, 50.8) : closestPaper(papers, 76.2, 50.8));

const online = (p: PnPrinter | undefined) => !!p && p.computer.state === 'connected';

/**
 * Printing with no print window, from any iPad, phone or computer: the store computer runs
 * PrintNode with the receipt printer and the DYMO plugged in, and STORE OS sends each receipt and
 * label to it. The PrintNode key stays on the STORE OS server.
 */
function PrintersTab() {
  const s = useLoad<any>(() => get('/api/settings'), []);
  const { me } = useSession();
  const here = me?.register?.location_id ?? me?.location?.id ?? null;
  const cloud = useCloudPrint();
  const { run, busy } = useAction();
  const [key, setKey] = useState('');
  const keySet = !!s.data && (!!s.data.printnode_api_key || s.data.printnode_api_key_env === '1');
  const list = useLoad<{ printers: PnPrinter[]; stores: PnStore[] } | null>(() => (keySet ? get('/api/printing/printers') : Promise.resolve(null)), [keySet, s.data?.printnode_api_key]);
  const [stores, setStores] = useState<PnStore[] | null>(null);
  useEffect(() => { if (list.data) setStores(list.data.stores); }, [list.data]);
  if (!s.data) return <Spinner />;
  const saveKey = async () => {
    const r = await run(() => patch('/api/settings', { printnode_api_key: key.trim() }), { success: 'PrintNode key saved.' });
    if (r) { setKey(''); s.reload(); cloud.refreshStatus(); }
  };
  return (
    <div className="stack">
      <AfterSaleCard on={s.data.auto_print_receipt === '1'} onSaved={() => s.reload()} />
      <TechTicketCard current={isTechTicketFormat(s.data.tech_ticket_format) ? s.data.tech_ticket_format : 'both'} onSaved={() => s.reload()} />
      <PriceStickerCard current={priceStickerSize(s.data.price_sticker_size)} shiftMm={priceStickerShiftMm(s.data.price_sticker_shift_mm)} onSaved={() => s.reload()} />
      <div className="card stack">
        <h2>Print with no print window (PrintNode)</h2>
        <p className="small muted" style={{ margin: 0 }}>
          Receipts, technician labels and price stickers print straight to the store’s printers from any iPad, phone or computer, with no print window.
          A receipt comes out exactly as long as it is and is cut right after “All rights reserved.” The technician label prints on the DYMO at 89 × 29 mm, and price stickers on the sticker printer at the size picked above (3 × 2 in, or 4 × 3 in sideways).
          The cash drawer plugged into the receipt printer opens by itself on cash sales, cash refunds, cash added or taken out and “No sale”.
          Until a store’s printers are picked below, it prints from the browser like before.
        </p>
        <ol className="printer-steps small">
          <li>On the store computer the printers are plugged into, PrintNode must be installed and signed in (RepairDesk already uses it there). Keep that computer on and PrintNode running while the store is open.</li>
          <li>Sign in at printnode.com with that same PrintNode account, open Account → API Keys, make a new key named STORE OS, and paste it below yourself. Using the same account keeps RepairDesk printing too.</li>
          <li>Pick each store’s receipt printer, label printer and price sticker printer, Save, and print a test of each. “Open the drawer” checks the cash drawer.</li>
        </ol>
        <div className="stack-sm">
          <span className="small strong">PrintNode API key</span>
          <div className="row" style={{ gap: 8 }}>
            <input className="input grow" type="password" value={key} onChange={(e) => setKey(e.target.value)} autoComplete="off" aria-label="PrintNode API key"
              placeholder={s.data.printnode_api_key ? 'Saved. Paste a new key to replace it' : s.data.printnode_api_key_env === '1' ? 'Using the key set in Render (PRINTNODE_API_KEY)' : 'Paste the API key'} />
            <Button variant="primary" busy={busy} disabled={key.trim().length < 16} onClick={saveKey}>Save key</Button>
          </div>
          {s.data.printnode_api_key ? <div><Badge tone="green" icon="check">Key saved ({s.data.printnode_api_key})</Badge></div> : null}
          <span className="tiny muted">It stays on the STORE OS server. iPads, phones and the counter computer never see it.</span>
        </div>
      </div>

      {keySet ? (
        <div className="card stack">
          <div className="spread">
            <h2>Each store’s printers</h2>
            <Button size="sm" icon="refresh" onClick={() => list.reload()}>Refresh</Button>
          </div>
          {list.loading && !list.data ? <Spinner label="Asking PrintNode for the printers" /> : null}
          {list.error ? <div className="banner bad" role="alert"><Icon.alert /><div>{list.error.message}</div></div> : null}
          {list.data && !list.data.printers.length ? (
            <div className="banner warn"><Icon.alert /><div>PrintNode doesn’t show any printers on this account yet. Open PrintNode on the store computer and sign in with the same account.</div></div>
          ) : null}
          {list.data && stores ? stores.map((st) => <PrinterStore key={st.location_id} store={st} printers={list.data!.printers} onSaved={setStores} suggest={st.location_id === here} stickerSize={priceStickerSize(s.data.price_sticker_size)} />) : null}
        </div>
      ) : null}
    </div>
  );
}

/** "Print the receipt after every sale": the "Sale complete" screen prints it by itself, once. */
function AfterSaleCard({ on, onSaved }: { on: boolean; onSaved: () => void }) {
  const { refresh } = useSession();
  const { run, busy } = useAction();
  // Ticked right away; back the way it was if it doesn't save.
  const [value, setValue] = useState(on);
  useEffect(() => setValue(on), [on]);
  const set = async (next: boolean) => {
    setValue(next);
    const r = await run(() => patch('/api/settings', { auto_print_receipt: next ? '1' : '0' }), { success: next ? 'Saved. The receipt prints after every sale.' : 'Saved. Receipts print when you tap Print receipt.' });
    if (r) { onSaved(); refresh(); } else setValue(on);
  };
  return (
    <div className="card stack-sm">
      <h2>After a sale</h2>
      <label className="check">
        <input type="checkbox" checked={value} disabled={busy} onChange={(e) => set(e.target.checked)} />
        Print the receipt after every sale
      </label>
      <p className="small muted" style={{ margin: 0 }}>The “Sale complete” screen prints the receipt by itself, once: on the store’s receipt printer through PrintNode when it’s set up below, otherwise in the print window. Off: tap Print receipt when the customer wants one.</p>
    </div>
  );
}

/** Which technician ticket "Print Technician Label" prints: the small DYMO label, the 4-inch one, or receipt paper. */
function TechTicketCard({ current, onSaved }: { current: TechTicketFormat; onSaved: () => void }) {
  const { refresh } = useSession();
  const { run, busy } = useAction();
  const pick = async (v: TechTicketFormat) => {
    if (v === current) return;
    const r = await run(() => patch('/api/settings', { tech_ticket_format: v }), { success: 'Saved. The technician ticket prints this way now.' });
    if (r) { onSaved(); refresh(); }
  };
  const subs: Record<TechTicketFormat, string> = {
    both: 'One tap prints the receipt-paper tech ticket on the receipt printer and the 4-inch TECH TICKET label on the DYMO (load the 4-inch 30256 labels).',
    receipt: 'The same design as the customer’s repair ticket, marked TECH REPAIR TICKET: the details, the drop-off check and the repairs. Ends with “Don’t Share Passcode”.',
    'label-4': 'The TECH TICKET label: logo, ticket number and barcode, when it’s due, customer, device and IMEI, the problem, the work to do, the drop-off check and the damage.',
    label: 'RepairDesk’s size: logo, ticket number and barcode, when it’s due, the customer, the device, the work and anything not working.',
  };
  return (
    <div className="card stack">
      <h2>Technician ticket</h2>
      <p className="small muted" style={{ margin: 0 }}>What “Print Technician Label” prints after a repair is checked in. Every option has your logo.</p>
      <div className="tt-formats" role="radiogroup" aria-label="Technician ticket">
        {(Object.keys(TECH_TICKET_FORMATS) as TechTicketFormat[]).map((k) => (
          <button key={k} type="button" role="radio" aria-checked={current === k} className={`tt-format ${current === k ? 'on' : ''}`} disabled={busy} onClick={() => pick(k)}>
            <span className="t">{TECH_TICKET_FORMATS[k]}</span>
            <span className="s">{subs[k]}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

/** What the price sticker printer (the Rollo) is loaded with: 3 × 2 in stickers, or 4 × 3 in labels (the sticker prints sideways on them, one a label). */
function PriceStickerCard({ current, shiftMm, onSaved }: { current: PriceStickerSize; shiftMm: number; onSaved: () => void }) {
  const { refresh } = useSession();
  const { run, busy } = useAction();
  // Move it right: the store's printer prints a little to the left of the label.
  const [shift, setShift] = useState(String(shiftMm));
  useEffect(() => setShift(String(shiftMm)), [shiftMm]);
  const shiftOk = /^\d{1,2}(\.\d)?$/.test(shift.trim()) && Number(shift) <= 20;
  const saveShift = async () => {
    const mm = shift.trim();
    const r = await run(() => patch('/api/settings', { price_sticker_shift_mm: mm }), { success: Number(mm) ? `Saved. Price stickers print ${mm} mm to the right now.` : 'Saved. Price stickers print without moving now.' });
    if (r) { onSaved(); refresh(); }
  };
  const pick = async (v: PriceStickerSize) => {
    if (v === current) return;
    const r = await run(() => patch('/api/settings', { price_sticker_size: v }), { success: 'Saved. Price stickers print at this size now.' });
    if (r) { onSaved(); refresh(); }
  };
  const subs: Record<PriceStickerSize, string> = {
    '3x2': 'The Rollo loaded with 3 × 2 in stickers (3 in across): the logo and the price on top, the model, color, carrier and battery, and the IMEI barcode along the bottom.',
    '2x3': 'The Rollo loaded with 2 × 3 in labels (2 in across, 3 in long): the same sticker turned sideways to fill the label, one sticker a label. In the print window pick the Rollo and its 2 × 3 in paper.',
    '2x2': 'A square sticker on the top of each 2 × 3 in label, one label a sticker: for a Rollo on Windows’ built-in driver, which has a 2x2in paper but no 2 × 3. In the print window pick the Rollo and its 2x2in paper (Chrome remembers it).',
    '4x3': 'The Rollo loaded with 4 × 3 in labels: the same sticker a third bigger, printed sideways across the label (4 in wide), one sticker a label. Then pick the 4 × 3 in paper under the store’s price sticker printer below.',
  };
  return (
    <div className="card stack">
      <h2>Price sticker</h2>
      <p className="small muted" style={{ margin: 0 }}>The label loaded in the price sticker printer. A phone’s price sticker prints on it, one a label, from Inventory, Phone check and a bought phone.</p>
      <div className="tt-formats" role="radiogroup" aria-label="Price sticker size">
        {(Object.keys(PRICE_STICKER_SIZES) as PriceStickerSize[]).map((k) => (
          <button key={k} type="button" role="radio" aria-checked={current === k} className={`tt-format ${current === k ? 'on' : ''}`} disabled={busy} onClick={() => pick(k)}>
            <span className="t">{PRICE_STICKER_SIZES[k]}</span>
            <span className="s">{subs[k]}</span>
          </button>
        ))}
      </div>
      <div className="stack-sm">
        <span className="small strong">Move it right</span>
        <span className="small muted">If the printer cuts off the left side of the sticker (the W of the logo), move everything right. The right margin gives way first; 0 = no move.</span>
        <div className="row" style={{ gap: 8 }}>
          <input className="input" style={{ width: 96 }} inputMode="decimal" value={shift} onChange={(e) => setShift(e.target.value.replace(/[^\d.]/g, '').slice(0, 4))} aria-label="Move the price sticker right, in mm" aria-invalid={!shiftOk} />
          <span className="small">mm</span>
          <Button variant="primary" busy={busy} disabled={!shiftOk || Number(shift) === shiftMm} onClick={saveShift}>Save</Button>
        </div>
      </div>
    </div>
  );
}

function PrinterStore({ store, printers, onSaved, suggest, stickerSize }: { store: PnStore; printers: PnPrinter[]; onSaved: (stores: PnStore[]) => void; suggest: boolean; stickerSize: PriceStickerSize }) {
  const cloud = useCloudPrint();
  const { run, busy } = useAction();
  // Nothing picked yet for the store this computer is in: suggest the POS-80, the DYMO and the
  // Rollo (saved only when Save is tapped). Other stores have their own computer, so nothing is guessed there.
  const fresh = suggest && !store.receipt && !store.label && !store.sticker;
  const [receiptId, setReceiptId] = useState(String(store.receipt?.id ?? (fresh ? printers.find((p) => /pos-?80(?!c)/i.test(p.name))?.id ?? '' : '')));
  const [dots, setDots] = useState(store.receipt?.dots ?? 576);
  const [labelId, setLabelId] = useState(String(store.label?.id ?? (fresh ? printers.find((p) => /dymo|labelwriter/i.test(p.name))?.id ?? '' : '')));
  const [stickerId, setStickerId] = useState(String(store.sticker?.id ?? (fresh ? printers.find((p) => /rollo/i.test(p.name))?.id ?? '' : '')));
  const labelPrinter = printers.find((p) => String(p.id) === labelId);
  const receiptPrinter = printers.find((p) => String(p.id) === receiptId);
  const stickerPrinter = printers.find((p) => String(p.id) === stickerId);
  const [paper, setPaper] = useState(store.label?.paper ?? closestLabelPaper(labelPrinter?.papers ?? []));
  const [rotate, setRotate] = useState(store.label?.rotate ?? 0);
  const [stickerPaper, setStickerPaper] = useState(store.sticker?.paper ?? closestStickerPaper(stickerPrinter?.papers ?? [], stickerSize));
  const [stickerRotate, setStickerRotate] = useState(store.sticker?.rotate ?? 0);
  const [openDrawer, setOpenDrawer] = useState(store.open_drawer !== false);
  const pickLabel = (id: string) => {
    setLabelId(id);
    setPaper(closestLabelPaper(printers.find((p) => String(p.id) === id)?.papers ?? []));
  };
  const pickSticker = (id: string) => {
    setStickerId(id);
    setStickerPaper(closestStickerPaper(printers.find((p) => String(p.id) === id)?.papers ?? [], stickerSize));
  };
  const changed = String(store.receipt?.id ?? '') !== receiptId || (store.receipt?.dots ?? 576) !== dots
    || String(store.label?.id ?? '') !== labelId || (store.label?.paper ?? '') !== paper || (store.label?.rotate ?? 0) !== rotate
    || String(store.sticker?.id ?? '') !== stickerId || (store.sticker?.paper ?? '') !== stickerPaper || (store.sticker?.rotate ?? 0) !== stickerRotate
    || (store.open_drawer !== false) !== openDrawer;
  const save = async () => {
    const body = {
      receipt: receiptPrinter ? { id: receiptPrinter.id, name: receiptPrinter.name, computer: receiptPrinter.computer.name || null, dots } : null,
      label: labelPrinter ? { id: labelPrinter.id, name: labelPrinter.name, computer: labelPrinter.computer.name || null, paper: paper || null, rotate } : null,
      sticker: stickerPrinter ? { id: stickerPrinter.id, name: stickerPrinter.name, computer: stickerPrinter.computer.name || null, paper: stickerPaper || null, rotate: stickerRotate } : null,
      openDrawer,
    };
    const r = await run(() => patch(`/api/printing/stores/${store.location_id}`, body), { success: `Printers saved for ${store.name}.` });
    if (r) { onSaved(r.stores); cloud.refreshStatus(); }
  };
  const option = (p: PnPrinter) => <option key={p.id} value={p.id}>{p.name}{p.computer.name ? ` · ${p.computer.name}` : ''}{online(p) ? '' : ' (computer offline)'}</option>;
  const computerBadge = (p: PnPrinter | undefined) => (!p ? null : online(p)
    ? <Badge tone="green" icon="check">{p.computer.name || 'Store computer'} is on</Badge>
    : <Badge tone="amber" icon="alert">{p.computer.name || 'The store computer'} is off or PrintNode isn’t running</Badge>);
  const computers = [receiptPrinter, labelPrinter, stickerPrinter].filter((p): p is PnPrinter => !!p).filter((p, i, all) => all.findIndex((x) => x.computer.id === p.computer.id) === i);
  const turn = (value: number, set: (v: number) => void, disabled: boolean, label: string) => (
    <Field label={label} hint="If it comes out sideways or upside down">
      <select className="select" value={value} onChange={(e) => set(Number(e.target.value))} disabled={disabled}>
        <option value={0}>Don’t turn it</option>
        <option value={90}>90°</option>
        <option value={180}>180° (upside down)</option>
        <option value={270}>270°</option>
      </select>
    </Field>
  );
  return (
    <section className="printer-store" aria-label={`Printers for ${store.name}`}>
      <div className="spread">
        <h3 style={{ margin: 0 }}>{store.name}</h3>
        {changed ? <Badge tone="amber">Not saved yet</Badge> : store.receipt || store.label || store.sticker ? <Badge tone="green" icon="check">Saved</Badge> : <Badge>Prints from the browser</Badge>}
      </div>
      <div className="printer-pick">
        <Field label="Receipt printer (80 mm)">
          <select className="select" value={receiptId} onChange={(e) => setReceiptId(e.target.value)}>
            <option value="">None: print receipts from the browser</option>
            {printers.map(option)}
          </select>
        </Field>
        <Field label="Receipt paper" hint="POS-80: 80 mm">
          <select className="select" value={dots} onChange={(e) => setDots(Number(e.target.value))} disabled={!receiptId}>
            <option value={576}>80 mm (576 dots)</option>
            <option value={512}>80 mm, narrower print (512 dots)</option>
            <option value={384}>58 mm (384 dots)</option>
          </select>
        </Field>
      </div>
      <label className="check small">
        <input type="checkbox" checked={!!receiptId && openDrawer} disabled={!receiptId} onChange={(e) => setOpenDrawer(e.target.checked)} />
        Open the cash drawer by itself (it’s plugged into the receipt printer): on cash sales, cash refunds, cash added or taken out, and “No sale”
      </label>
      <div className="printer-pick">
        <Field label="Technician label printer">
          <select className="select" value={labelId} onChange={(e) => pickLabel(e.target.value)}>
            <option value="">None: print labels from the browser</option>
            {printers.map(option)}
          </select>
        </Field>
        <Field label="Label" hint="89 × 29 mm, like RepairDesk: DYMO 30252 Address">
          <select className="select" value={paper} onChange={(e) => setPaper(e.target.value)} disabled={!labelId}>
            <option value="">The printer’s usual label</option>
            {(labelPrinter?.papers ?? []).map((x) => <option key={x.name} value={x.name}>{x.name}{x.w && x.h ? ` (${x.w} × ${x.h} mm)` : ''}</option>)}
          </select>
        </Field>
        {turn(rotate, setRotate, !labelId, 'Turn the label')}
      </div>
      <div className="printer-pick">
        <Field label="Price sticker printer" hint={stickerSize === '4x3' ? '4 × 3 in labels, like the Rollo' : stickerSize === '2x3' ? '2 × 3 in labels, like the Rollo' : stickerSize === '2x2' ? '2 × 2 in square stickers on the Rollo' : '3 × 2 in stickers, like the Rollo'}>
          <select className="select" value={stickerId} onChange={(e) => pickSticker(e.target.value)}>
            <option value="">None: print stickers from the browser</option>
            {printers.map(option)}
          </select>
        </Field>
        <Field label="Sticker" hint={stickerSize === '4x3' ? '4 × 3 in (102 × 76 mm)' : stickerSize === '2x3' ? '2 × 3 in (51 × 76 mm)' : stickerSize === '2x2' ? '2 × 2 in (51 × 51 mm)' : '3 × 2 in (76 × 51 mm)'}>
          <select className="select" value={stickerPaper} onChange={(e) => setStickerPaper(e.target.value)} disabled={!stickerId}>
            <option value="">The printer’s usual sticker</option>
            {(stickerPrinter?.papers ?? []).map((x) => <option key={x.name} value={x.name}>{x.name}{x.w && x.h ? ` (${x.w} × ${x.h} mm)` : ''}</option>)}
          </select>
        </Field>
        {turn(stickerRotate, setStickerRotate, !stickerId, 'Turn the sticker')}
      </div>
      {computers.length ? <div className="row-wrap" style={{ gap: 8 }}>{computers.map((p) => <span key={p.computer.id}>{computerBadge(p)}</span>)}</div> : null}
      <div className="row-wrap" style={{ gap: 8 }}>
        <Button variant="primary" busy={busy} disabled={!changed} onClick={save}>Save</Button>
        <Button icon="printer" disabled={!store.receipt || changed} onClick={() => cloud.printOut({ kind: 'test_receipt', refId: store.location_id, title: `Test receipt · ${store.name}`, printer: store.receipt?.name })}>Print a test receipt</Button>
        <Button icon="tag" disabled={!store.label || changed} onClick={() => cloud.printOut({ kind: 'test_label', refId: store.location_id, title: `Test label · ${store.name}`, printer: store.label?.name })}>Print a test label</Button>
        <Button icon="tag" disabled={!store.sticker || changed} onClick={() => cloud.printOut({ kind: 'test_sticker', refId: store.location_id, title: `Test price sticker · ${store.name}`, printer: store.sticker?.name })}>Print a test sticker</Button>
        <Button icon="cash" disabled={!store.receipt || changed} onClick={() => cloud.printOut({ kind: 'test_drawer', refId: store.location_id, title: `Cash drawer · ${store.name}`, printer: store.receipt?.name })}>Open the drawer</Button>
      </div>
    </section>
  );
}
